  // @ts-nocheck
  /**
   * Premium PDP — gallery + variant table integration.
   * Reuses shared weight/cart logic from plp-table-details.js.
   */
  (function () {
    'use strict';

    var PDP_MAIN_WIDTHS = [375, 550, 750, 900, 1100, 1300, 1500, 1600, 1800, 2000];
    var PDP_MAIN_SIZES = '(min-width: 990px) 50vw, 100vw';
    var productLoadPromiseById = new Map();
    var controllersByRoot = new WeakMap();
    /** @type {WeakMap<HTMLElement, number>} */
    var hydrateGenerationByRoot = new WeakMap();

    function parseProduct(root) {
      var el = root.querySelector('[data-pdp-product-json]');
      if (!el || !el.textContent) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    /**
     * @param {HTMLElement} root
     * @param {string} selector
     * @returns {unknown}
     */
    function readJsonScript(root, selector) {
      var el = root.querySelector(selector);
      if (!el || !el.textContent) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    /**
     * @param {any} product
     * @returns {string[]}
     */
    function linkedFamilyIds(product) {
      var api = window.AoneLinkedProducts;
      if (api && typeof api.collectProductIds === 'function') {
        var ids = api.collectProductIds(product);
        if (Array.isArray(ids) && ids.length) return ids;
      }
      var id = normalizeShopifyId(product && product.id);
      return id ? [id] : [];
    }

    /**
     * @param {any} product
     * @returns {string[]}
     */
    function linkedFamilyHandles(product) {
      var api = window.AoneLinkedProducts;
      if (api && typeof api.collectHandles === 'function') {
        var handles = api.collectHandles(product);
        if (Array.isArray(handles) && handles.length) return handles;
      }
      var handle = String((product && product.handle) || '').trim();
      return handle ? [handle] : [];
    }

    /**
     * Fold custom.linked_products into the page product before the matrix is built.
     * @param {HTMLElement} root
     * @param {any} product
     * @returns {any}
     */
    function applyLinkedFamilyToProduct(root, product) {
      if (!product || product.__linkedFamilyMerged === true) return product;
      var api = window.AoneLinkedProducts;
      if (!api || typeof api.mergeFamily !== 'function') return product;
      var linked = readJsonScript(root, '[data-pdp-linked-products-json]');
      if (!Array.isArray(linked) || !linked.length) return product;
      var merged = api.mergeFamily(product, linked);
      if (!merged) return product;
      var el = root.querySelector('[data-pdp-product-json]');
      if (el) {
        try {
          el.textContent = JSON.stringify(merged);
        } catch (_err) {
          /* keep the in-memory product even if the script tag cannot be rewritten */
        }
      }
      return merged;
    }

    /**
     * @param {any} product
     * @returns {Array<{ name: string, position: number, displayLabel: string, key: string }>}
     */
    function optionDefsFromProduct(product) {
      var options = Array.isArray(product && product.options) ? product.options : [];
      /** @type {Array<{ name: string, position: number, displayLabel: string, key: string }>} */
      var defs = [];
      options.forEach(function (opt, index) {
        var name = '';
        if (typeof opt === 'string') name = opt;
        else if (opt && typeof opt === 'object' && opt.name != null) name = String(opt.name);
        name = name.trim();
        if (!name) return;
        var key = normalizeKey(name);
        var displayLabel = name.toUpperCase();
        var storedKey = key;
        if (isWidthAxisKey(key)) {
          storedKey = 'width';
          displayLabel = 'WIDTH';
        } else if (isLengthAxisKey(key)) {
          storedKey = 'length';
          displayLabel = 'LENGTH';
        } else if (isMaterialAxisKey(key)) {
          storedKey = 'material';
          displayLabel = 'MATERIAL';
        }
        defs.push({
          name: name,
          position: index + 1,
          displayLabel: displayLabel,
          key: storedKey,
        });
      });
      return defs;
    }

    function getWmsApi() {
      return window.WMSInventory || window.AOneWmsAvailability || null;
    }

    /** @type {Record<string, Promise<Record<string, number>>>} */
    var shopifyStockPromiseByHandle = Object.create(null);

    /**
     * Shopify inventory qty for a variant (never WMS).
     * @param {any} variant
     * @returns {number}
     */
    function isVariantShopifyQtyKnown(variant) {
      if (!variant || typeof variant !== 'object') return false;
      if (Number.isFinite(Number(variant.shopify_inventory_quantity))) return true;
      return Number.isFinite(Number(variant.inventory_quantity));
    }

    function resolveVariantShopifyQty(variant) {
      if (!isVariantShopifyQtyKnown(variant)) return 0;
      var preserved = Number(variant.shopify_inventory_quantity);
      if (Number.isFinite(preserved) && preserved >= 0) return Math.floor(preserved);
      var qty = Number(variant.inventory_quantity);
      if (Number.isFinite(qty) && qty >= 0) return Math.floor(qty);
      return 0;
    }

    /**
     * Prefer Liquid inventory_quantity when present, then stamp shopify_inventory_quantity.
     * @param {any} product
     */
    function stampShopifyQtyFromLiquidVariants(product) {
      if (!product || !Array.isArray(product.variants)) return;
      product.variants.forEach(function (variant) {
        if (!variant || typeof variant !== 'object') return;
        if (Number.isFinite(Number(variant.shopify_inventory_quantity))) return;
        var qty = Number(variant.inventory_quantity);
        if (!Number.isFinite(qty) || qty < 0) return;
        variant.shopify_inventory_quantity = Math.floor(qty);
      });
    }

    /**
     * Fetch Shopify inventory map via product.qty view (same source as QV / Order sheet).
     * @param {string} handle
     * @returns {Promise<Record<string, number>>}
     */
    function fetchShopifyStockMapForHandle(handle) {
      var safeHandle = String(handle || '').trim();
      if (!safeHandle) return Promise.resolve({});
      if (shopifyStockPromiseByHandle[safeHandle]) return shopifyStockPromiseByHandle[safeHandle];

      var req = fetch('/products/' + encodeURIComponent(safeHandle) + '?view=qty', {
        headers: { Accept: 'application/json, text/plain, */*' },
      })
        .then(function (res) {
          if (!res.ok) throw new Error('[PDP] qty view HTTP ' + res.status);
          return res.text();
        })
        .then(function (rawText) {
          var parsed = JSON.parse(String(rawText || '').trim() || '{}');
          /** @type {Record<string, number>} */
          var map = {};
          if (!parsed || typeof parsed !== 'object') return map;
          Object.keys(parsed).forEach(function (key) {
            if (String(key).charAt(0) === '_') return;
            var n = Number(parsed[key]);
            if (!Number.isFinite(n)) return;
            map[String(key)] = Math.max(0, Math.floor(n));
          });
          return map;
        })
        .catch(function (err) {
          delete shopifyStockPromiseByHandle[safeHandle];
          warnPdpDev('PDP Shopify qty view failed; stock display may be 0', {
            handle: safeHandle,
            error: err && err.message ? err.message : err,
          });
          return {};
        });

      shopifyStockPromiseByHandle[safeHandle] = req;
      return req;
    }

    /**
     * Merge Shopify qty onto product.variants (never overwrite with WMS).
     * @param {any} product
     * @param {Record<string, number>} stockMap
     */
    function applyShopifyStockMapToProduct(product, stockMap) {
      if (!product || !Array.isArray(product.variants) || !stockMap) return;
      product.variants.forEach(function (variant) {
        if (!variant || variant.id == null) return;
        var qty = stockMap[String(variant.id)];
        if (!Number.isFinite(Number(qty))) return;
        var safe = Math.max(0, Math.floor(Number(qty)));
        variant.inventory_quantity = safe;
        variant.shopify_inventory_quantity = safe;
      });
    }

    /**
     * Ensure PDP product variants carry Shopify inventory for IN-STOCK / COMING SOON.
     * @param {HTMLElement} root
     * @param {any} product
     * @returns {Promise<any>}
     */
    function ensureShopifyInventoryOnProduct(root, product) {
      stampShopifyQtyFromLiquidVariants(product);
      var handles = linkedFamilyHandles(product);
      if (!handles.length) return Promise.resolve(product);

      return Promise.all(
        handles.map(function (handle) {
          return fetchShopifyStockMapForHandle(handle);
        })
      ).then(function (maps) {
        var stockMap = Object.assign.apply(null, [{}].concat(maps || []));
        if (stockMap && Object.keys(stockMap).length) {
          applyShopifyStockMapToProduct(product, stockMap);
        }
        // Persist so cart rehydrate / parseProduct keep Shopify qty.
        var el = root && root.querySelector ? root.querySelector('[data-pdp-product-json]') : null;
        if (el) {
          try {
            el.textContent = JSON.stringify(product);
          } catch (_err) {
            /* noop */
          }
        }
        return product;
      });
    }

  /**
   * Stock status label from Shopify qty only (never WMS / products-batch).
   * Matrix uses IN-STOCK / COMING SOON. Table stock cells may still prefer a bare number / N/A.
   * @param {number} shopifyQty
   * @param {{ numericOnly?: boolean }} [options]
   * @returns {{ key: string, label: string, quantity: number }}
   */
  function getShopifyDisplayStockStatus(shopifyQty, options) {
    options = options || {};
    var quantity = Math.max(0, Math.floor(Number(shopifyQty) || 0));
    if (quantity > 0) {
      return {
        key: 'available',
        label: options.numericOnly ? String(quantity) : 'IN-STOCK: ' + quantity,
        quantity: quantity,
      };
    }
    return {
      key: 'coming-soon',
      label: options.numericOnly ? 'N/A' : 'COMING SOON',
      quantity: 0,
    };
  }

    function normalizeSku(value) {
      var api = getWmsApi();
      if (api && typeof api.normalizeSku === 'function') return api.normalizeSku(value);
      return String(value || '')
        .trim()
        .toUpperCase();
    }

    function normalizeSkuDisplay(value) {
      return String(value || '').trim();
    }

    function normalizeShopifyId(value) {
      var api = getWmsApi();
      if (api && typeof api.normalizeId === 'function') return api.normalizeId(value);
      if (value === null || value === undefined) return '';
      var stringValue = String(value).trim();
      if (!stringValue) return '';
      if (stringValue.indexOf('/') >= 0) return stringValue.split('/').pop() || '';
      return stringValue;
    }

    function warnPdpDev(message, meta) {
      var api = getWmsApi();
      if (api && typeof api.warnDev === 'function') {
        api.warnDev(message, meta);
        return;
      }
      if (typeof console === 'undefined' || typeof console.warn !== 'function') return;
      if (meta !== undefined) console.warn('[A-One PDP WMS]', message, meta);
      else console.warn('[A-One PDP WMS]', message);
    }

    function toFiniteNumber(value) {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string') {
        var match = value.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
        if (!match) return null;
        var n = Number(match[0]);
        return Number.isFinite(n) ? n : null;
      }
      return null;
    }

    function formatWeight(weight) {
      var n = Number(weight);
      if (!Number.isFinite(n) || n <= 0) return '—';
      return n.toFixed(2) + 'g';
    }

    function getSharedWeightStore() {
      if (!window.__aoneWeightSharedStore) {
        window.__aoneWeightSharedStore = {
          rawBySku: Object.create(null),
          dataBySku: Object.create(null),
          pendingBySku: Object.create(null),
          pendingBatchByKey: Object.create(null),
        };
      }
      return window.__aoneWeightSharedStore;
    }

    function extractFromList(list, skuNeedle) {
      if (!Array.isArray(list)) return null;
      var needle = normalizeSku(skuNeedle);
      if (!needle) return null;
      for (var i = 0; i < list.length; i += 1) {
        var item = list[i];
        if (!item || typeof item !== 'object') continue;
        var sku = normalizeSku(item.sku || item.SKU || item.itemSku || item.item_sku);
        if (sku && sku === needle) return item;
      }
      return null;
    }

    function getStoreSkuEntry(store, sku) {
      if (!store || !store.skus) return null;
      var safeSku = normalizeSkuDisplay(sku);
      var key = normalizeSku(sku);
      if (!key && !safeSku) return null;
      var entry =
        (safeSku && store.skus.get(safeSku)) ||
        (key && store.skus.get(key)) ||
        (safeSku && store.skus.get(safeSku.toLowerCase())) ||
        null;
      if (entry) return entry;
      if (typeof store.skus.forEach !== 'function') return null;
      var matched = null;
      store.skus.forEach(function (value, mapKey) {
        if (matched) return;
        if (normalizeSku(mapKey) === key) matched = value;
      });
      return matched;
    }

    function getCachedNodeForSku(product, sku) {
      var displaySku = normalizeSkuDisplay(sku);
      var key = normalizeSku(sku);
      if (!key) return null;

      var wmsApi = getWmsApi();
      var productIdForWms = normalizeShopifyId(product && product.id);
      if (wmsApi && typeof wmsApi.getSkuAvailability === 'function' && productIdForWms) {
        var availability = wmsApi.getSkuAvailability(productIdForWms, key);
        if (
          availability &&
          (availability.loading ||
            availability.status === 'loading' ||
            availability.status === 'idle')
        ) {
          return { __pdpInventoryPending: true, sku: displaySku || key };
        }
        if (availability && availability.status === 'loaded') {
          return availability;
        }
      }
      if (
        wmsApi &&
        typeof wmsApi.isCartStateReady === 'function' &&
        !wmsApi.isCartStateReady()
      ) {
        return { __pdpInventoryPending: true, sku: displaySku || key };
      }
      if (wmsApi && typeof wmsApi.getInventoryBySku === 'function') {
        var inventory = wmsApi.getInventoryBySku(key);
        if (inventory && inventory.loading) {
          return { __pdpInventoryPending: true, sku: displaySku || key };
        }
        if (inventory && inventory.found !== false && !inventory.loading) {
          return {
            sku: inventory.sku,
            found: inventory.found,
            count: inventory.quantity,
            rawAvailableCount: inventory.rawAvailableCount,
            minWeight: inventory.minimumWeight,
            maxWeight: inventory.maximumWeight,
            weightGroups: inventory.weightGroups,
            availableOptions: inventory.availableOptions,
            storefrontStatus: inventory.storefrontStatus || inventory.status,
          };
        }
      }

      var shared = getSharedWeightStore();
      if (shared.rawBySku) {
        if (shared.rawBySku[key.toLowerCase()]) return shared.rawBySku[key.toLowerCase()];
        if (displaySku && shared.rawBySku[displaySku.toLowerCase()]) {
          return shared.rawBySku[displaySku.toLowerCase()];
        }
      }

      var store = window.AOneProductWeights;
      if (store) {
        var skuEntry = getStoreSkuEntry(store, displaySku || key);
        if (skuEntry && skuEntry.item) return skuEntry.item;

        var productId = normalizeShopifyId(product && product.id);
        var productNode = productId && store.products && store.products.get(productId);
        if (productNode) {
          var fromSkus = extractFromList(productNode.skus, key);
          if (fromSkus) return fromSkus;
          var fromItems = extractFromList(productNode.items, key);
          if (fromItems) return fromItems;
        }
      }

      return null;
    }

    /**
     * Normalize a cached WMS SKU node into a stable PDP row payload.
     * @param {unknown} node
     * @param {string} sku
     */
    function normalizePdpWmsSkuData(node, sku) {
      var safeSku = normalizeSkuDisplay(sku);
      if (!node || typeof node !== 'object') {
        return {
          sku: safeSku,
          found: false,
          count: 0,
          rawAvailableCount: 0,
          minWeight: null,
          maxWeight: null,
          weightGroups: [],
          availableOptions: [],
          storefrontStatus: 'COMING_SOON',
        };
      }
      var obj = node;
      var availableOptions = Array.isArray(obj.availableOptions)
        ? obj.availableOptions
        : Array.isArray(obj.availableWeights)
          ? obj.availableWeights
          : [];
      var found = obj.found !== false;
      var countRaw = found ? extractAvailableQtyFromNode(obj) : 0;
      var count = countRaw == null ? 0 : Math.max(0, Math.floor(countRaw));
      var optionsLen = found ? availableOptions.length : 0;
      if (!found) {
        availableOptions = [];
      }
      if (count > 0 && optionsLen > 0 && count !== optionsLen) {
        warnPdpDev('WMS count and availableOptions length differ; using safer lower max quantity', {
          sku: safeSku,
          count: count,
          availableOptions: optionsLen,
        });
        count = Math.min(count, optionsLen);
      } else if (count <= 0 && optionsLen > 0) {
        count = optionsLen;
      }

      var wmsApi = getWmsApi();
      var rawAvailableCount =
        obj.rawAvailableCount != null ? Math.max(0, Math.floor(Number(obj.rawAvailableCount) || 0)) : count;
      var storefrontStatus =
        obj.storefrontStatus ||
        (wmsApi && typeof wmsApi.getStorefrontStatus === 'function'
          ? wmsApi.getStorefrontStatus(rawAvailableCount, count)
          : count > 0
            ? 'AVAILABLE'
            : rawAvailableCount > 0
              ? 'ALL_IN_CART'
              : 'COMING_SOON');

      return {
        sku: safeSku,
        found: found,
        count: count,
        rawAvailableCount: rawAvailableCount,
        minWeight:
          found && (count > 0 || rawAvailableCount > 0)
            ? extractMinWeightFromNode(obj) ||
              extractMinWeightFromNode({
                availableOptions: Array.isArray(obj.rawAvailableOptions)
                  ? obj.rawAvailableOptions
                  : availableOptions,
              })
            : null,
        maxWeight:
          found && (count > 0 || rawAvailableCount > 0)
            ? toFiniteNumber(obj.maxWeight ?? obj.maximumWeight)
            : null,
        weightGroups: found && Array.isArray(obj.weightGroups) ? obj.weightGroups : [],
        availableOptions: availableOptions,
        rawAvailableOptions: Array.isArray(obj.rawAvailableOptions) ? obj.rawAvailableOptions : availableOptions,
        storefrontStatus: storefrontStatus,
      };
    }

    function resolveMaxSelectableQuantity(wmsSkuData) {
      var count = Math.max(0, Math.floor(Number(wmsSkuData && wmsSkuData.count) || 0));
      var optionsLen = Array.isArray(wmsSkuData && wmsSkuData.availableOptions)
        ? wmsSkuData.availableOptions.length
        : 0;
      if (optionsLen > 0 && count > 0) return Math.min(count, optionsLen);
      if (optionsLen > 0) return optionsLen;
      return count;
    }

    /**
     * Central PDP row binder: visible text + datasets used by weight modal / ATC.
     * @param {HTMLElement} rowElement
     * @param {ReturnType<typeof normalizePdpWmsSkuData> | null} wmsSkuData
     * @param {{ isLoading?: boolean, variant?: any }} [options]
     */
    function updatePdpVariantRowFromWms(rowElement, wmsSkuData, options) {
      options = options || {};
      var row = rowElement;
      if (!(row instanceof HTMLElement)) return;

      var minWeightCell = row.querySelector('[data-premium-pdp-min-weight]');
      var stockCell = row.querySelector('[data-premium-pdp-stock]');
      var weightBtn = row.querySelector('[data-details-weight-open]');
      var weightLabel = row.querySelector('[data-details-weight-label]');
      var isLoading = options.isLoading === true;
      var variant = options.variant || null;
      var shopifyFromVariant = variant != null && isVariantShopifyQtyKnown(variant);
      var rowQtyAttr = row.getAttribute('data-shopify-inventory-qty');
      var shopifyFromRow = rowQtyAttr != null && rowQtyAttr !== '' && Number.isFinite(Number(rowQtyAttr));
      var shopifyQty = shopifyFromVariant
        ? resolveVariantShopifyQty(variant)
        : shopifyFromRow
          ? Math.max(0, Math.floor(Number(rowQtyAttr)))
          : null;
      if (shopifyQty != null) {
        row.setAttribute('data-shopify-inventory-qty', String(shopifyQty));
      }
      var displaySku = normalizeSkuDisplay(
        (wmsSkuData && wmsSkuData.sku) || row.dataset.variantSku || ''
      );

      if (isLoading) {
        var loadingInStock = shopifyQty != null && shopifyQty > 0;
        row.dataset.wmsStatus = 'loading';
        row.dataset.rowState = 'loading';
        // Shopify qty enables the stepper immediately. Weight stays Checking… until products-batch.
        row.dataset.variantAvailable = loadingInStock ? 'true' : 'false';
        row.dataset.variantInventoryQty = loadingInStock ? String(shopifyQty) : '0';
        row.classList.toggle('is-unavailable', shopifyQty != null && !loadingInStock);
        row.setAttribute('aria-busy', 'true');
        if (minWeightCell) {
          minWeightCell.textContent = 'Checking...';
          minWeightCell.setAttribute('aria-busy', 'true');
        }
        if (stockCell) {
          if (shopifyQty == null) {
            stockCell.textContent = '';
            stockCell.setAttribute('aria-busy', 'true');
          } else {
            stockCell.textContent = loadingInStock ? String(shopifyQty) : '';
            stockCell.removeAttribute('aria-busy');
          }
        }
        if (weightLabel instanceof HTMLElement) {
          weightLabel.textContent = 'Checking...';
        }
        syncRowControls(row);
        return;
      }

      row.removeAttribute('aria-busy');
      if (minWeightCell) minWeightCell.removeAttribute('aria-busy');
      if (stockCell) stockCell.removeAttribute('aria-busy');

      if (!displaySku) {
        warnPdpDev('PDP variant row is missing a SKU; leaving row unavailable', {
          variantId: row.dataset.variantId || '',
        });
        row.dataset.wmsStatus = 'coming-soon';
        row.dataset.rowState = 'ready';
        row.dataset.variantAvailable = 'false';
        row.dataset.variantInventoryQty = '0';
        row.classList.add('is-unavailable');
        delete row.dataset.weightValue;
        delete row.dataset.serialNumbersValue;
        delete row.dataset.wmsOptionsJson;
        row.classList.remove('has-weight-selected');
        if (minWeightCell) minWeightCell.textContent = 'N/A';
        if (stockCell) {
          stockCell.textContent =
            shopifyQty != null && shopifyQty > 0 ? String(shopifyQty) : 'N/A';
        }
        if (weightLabel instanceof HTMLElement) weightLabel.textContent = 'N/A';
        syncRowControls(row);
        return;
      }

      var data = wmsSkuData || normalizePdpWmsSkuData(null, displaySku);
      var maxQty = resolveMaxSelectableQuantity(data);
      var rawQty = Math.max(
        0,
        Math.floor(Number(data.rawAvailableCount != null ? data.rawAvailableCount : data.count) || 0)
      );
      var wmsApi = getWmsApi();
      var storefrontStatus =
        data.storefrontStatus ||
        (wmsApi && typeof wmsApi.getStorefrontStatus === 'function'
          ? wmsApi.getStorefrontStatus(rawQty, maxQty)
          : maxQty > 0
            ? 'AVAILABLE'
            : rawQty > 0
              ? 'ALL_IN_CART'
              : 'COMING_SOON');
      var available = storefrontStatus === 'AVAILABLE' && maxQty > 0;
      var allInCart = storefrontStatus === 'ALL_IN_CART';
      var minWeight = data.minWeight;
      // Weight selection still needs a real WMS minimum. Stock visibility does not.
      var weightReady = available && minWeight != null && minWeight > 0;
      var shopifyInStock = shopifyQty != null && shopifyQty > 0;
      if (shopifyInStock) available = true;

      row.dataset.variantSku = displaySku;
      row.dataset.wmsStatus = shopifyInStock || available ? 'available' : allInCart ? 'all-in-cart' : 'coming-soon';
      row.dataset.rowState = 'ready';
      row.dataset.variantAvailable = shopifyInStock || available ? 'true' : 'false';
      // Stepper max is Shopify available quantity. WMS only supplies selectable weights.
      row.dataset.variantInventoryQty = String(shopifyInStock ? shopifyQty : maxQty);
      row.dataset.wmsRawAvailableQty = String(rawQty);
      row.classList.toggle('is-unavailable', !available && !allInCart);

      var optionsForRow =
        Array.isArray(data.availableOptions) && data.availableOptions.length
          ? data.availableOptions
          : Array.isArray(data.rawAvailableOptions) && data.rawAvailableOptions.length
            ? data.rawAvailableOptions
            : [];
      if ((weightReady || allInCart || shopifyInStock) && optionsForRow.length) {
        try {
          row.dataset.wmsOptionsJson = JSON.stringify(optionsForRow);
        } catch (_error) {
          delete row.dataset.wmsOptionsJson;
        }
      } else {
        delete row.dataset.wmsOptionsJson;
      }

      if (data.minWeight != null && data.minWeight > 0) {
        row.dataset.wmsMinWeight = String(data.minWeight);
      } else {
        delete row.dataset.wmsMinWeight;
      }
      if (data.maxWeight != null && data.maxWeight > 0) {
        row.dataset.wmsMaxWeight = String(data.maxWeight);
      } else {
        delete row.dataset.wmsMaxWeight;
      }

      if (minWeightCell) {
        minWeightCell.textContent =
          minWeight != null && minWeight > 0 && (weightReady || shopifyInStock || allInCart)
            ? formatWeight(minWeight)
            : 'N/A';
      }
      // Available Stock cell is Shopify-owned (qty number or N/A after resolve).
      if (stockCell) {
        var displayQty = shopifyQty != null ? shopifyQty : 0;
        stockCell.textContent = displayQty > 0 ? String(displayQty) : 'N/A';
      }

      if (!shopifyInStock && !available && !allInCart) {
        delete row.dataset.weightValue;
        delete row.dataset.serialNumbersValue;
        row.classList.remove('has-weight-selected');
        if (weightLabel instanceof HTMLElement) {
          weightLabel.textContent = 'N/A';
        }
        if (weightBtn instanceof HTMLElement) {
          weightBtn.setAttribute('data-weight-idle-label', 'N/A');
          weightBtn.classList.remove('is-confirmed');
        }
      } else if (weightLabel instanceof HTMLElement) {
        var selectedSerials = String(row.dataset.serialNumbersValue || '').trim();
        var minLabel =
          wmsApi && typeof wmsApi.formatMinimumWeightLabel === 'function'
            ? wmsApi.formatMinimumWeightLabel(minWeight)
            : minWeight != null && Number(minWeight) > 0
              ? 'Min wt: ' + formatWeight(minWeight)
              : 'N/A';
        var actionLabel = selectedSerials ? 'Edit weight' : minLabel;
        if (weightBtn instanceof HTMLElement) {
          weightBtn.setAttribute('data-weight-idle-label', actionLabel);
          if (selectedSerials) weightBtn.classList.add('is-confirmed');
          else weightBtn.classList.remove('is-confirmed');
        }
        weightLabel.textContent = actionLabel || 'N/A';
      }

      // Cart baseline first so qty max / Edit weight unlock before control sync.
      if (window.plpVariantDetails && typeof window.plpVariantDetails.applyCartSelectionToRow === 'function') {
        window.plpVariantDetails.applyCartSelectionToRow(row);
      }
      syncRowControls(row);
    }

    function extractMinWeightFromNode(node) {
      if (node == null) return null;
      var direct = toFiniteNumber(node);
      if (direct != null && direct > 0) return direct;
      if (Array.isArray(node)) {
        var min = null;
        for (var i = 0; i < node.length; i += 1) {
          var val = extractMinWeightFromNode(node[i]);
          if (val == null || val <= 0) continue;
          min = min == null ? val : Math.min(min, val);
        }
        return min;
      }
      if (typeof node !== 'object') return null;
      var obj = node;
      var keys = [
        'minWeight',
        'min_weight',
        'minimumWeight',
        'minimum_weight',
        'overallMinWeight',
        'weight',
        'weight_g',
      ];
      for (var k = 0; k < keys.length; k += 1) {
        var key = keys[k];
        if (!(key in obj)) continue;
        var candidate = extractMinWeightFromNode(obj[key]);
        if (candidate != null && candidate > 0) return candidate;
      }
      var nestedKeys = ['summary', 'data', 'payload', 'item', 'items', 'availableOptions', 'availableWeights'];
      for (var n = 0; n < nestedKeys.length; n += 1) {
        var nested = nestedKeys[n];
        if (!(nested in obj)) continue;
        var nestedVal = extractMinWeightFromNode(obj[nested]);
        if (nestedVal != null && nestedVal > 0) return nestedVal;
      }
      return null;
    }

    function extractAvailableQtyFromNode(node) {
      if (node == null) return null;
      if (Array.isArray(node)) return node.length;
      if (typeof node !== 'object') return null;
      var obj = node;
      var keys = [
        'count',
        'availableQty',
        'available_qty',
        'availableQuantity',
        'available_quantity',
        'totalAvailableQuantity',
        'stock',
        'qty',
        'quantity',
      ];
      for (var i = 0; i < keys.length; i += 1) {
        var key = keys[i];
        if (!(key in obj)) continue;
        var qty = toFiniteNumber(obj[key]);
        if (qty != null && qty >= 0) return Math.floor(qty);
      }
      var nestedKeys = ['availableOptions', 'availableWeights', 'items', 'data', 'summary'];
      for (var n = 0; n < nestedKeys.length; n += 1) {
        var nested = nestedKeys[n];
        if (!(nested in obj)) continue;
        var nestedQty = extractAvailableQtyFromNode(obj[nested]);
        if (nestedQty != null) return nestedQty;
      }
      return null;
    }

    /**
     * Wait until weight-service exposes products-batch API (script order can lag PDP boot).
     * @param {number} [timeoutMs]
     * @returns {Promise<any>}
     */
    function waitForProductWeightsApi(timeoutMs) {
      var existing = window.AOneProductWeightsAPI || window.AoneBatchWeight;
      if (existing && typeof existing.loadCurrentProductWeights === 'function') {
        return Promise.resolve(existing);
      }
      var maxMs = Number.isFinite(Number(timeoutMs)) ? Number(timeoutMs) : 8000;
      var started = Date.now();
      return new Promise(function (resolve) {
        var timer = window.setInterval(function () {
          var api = window.AOneProductWeightsAPI || window.AoneBatchWeight;
          if (api && typeof api.loadCurrentProductWeights === 'function') {
            window.clearInterval(timer);
            resolve(api);
            return;
          }
          if (Date.now() - started >= maxMs) {
            window.clearInterval(timer);
            resolve(null);
          }
        }, 50);
      });
    }

    /**
     * Block PDP "Checking..." until products-batch resolves for this parent product.
     * @param {any} product
     * @returns {Promise<unknown>}
     */
    function ensureWmsProductData(product) {
      var ids = linkedFamilyIds(product);
      var productId = ids[0] || normalizeShopifyId(product && product.id);
      if (!productId) return Promise.resolve();
      var requestKey = ids.join(',');
      if (productLoadPromiseById.has(requestKey)) return productLoadPromiseById.get(requestKey);

      var availabilityApi = getWmsApi();
      var cartPromise =
        availabilityApi && typeof availabilityApi.ensureCartSerialsSynced === 'function'
          ? availabilityApi
              .ensureCartSerialsSynced(
                !(availabilityApi.isCartStateReady && availabilityApi.isCartStateReady())
              )
              .catch(function () {
                return null;
              })
          : Promise.resolve(null);

      var loadPromise = waitForProductWeightsApi(8000).then(function (api) {
        if (!api || typeof api.loadCurrentProductWeights !== 'function') {
          warnPdpDev('PDP products-batch API missing; leaving Checking after timeout', {
            productId: productId,
          });
          return false;
        }
        // PDP load/rehydrate: fetch once, then reuse AOneProductWeights for this parent.
        // preferCache prevents Add-to-Cart → cart inventory rehydrate from POSTing products-batch again.
        // Cold cache (first paint / new product) still networks; in-flight requests still dedupe.
        return api
          .loadCurrentProductWeights(ids, { source: 'pdp', preferCache: true })
          .then(function () {
            return true;
          });
      });

      var req = Promise.all([loadPromise, cartPromise])
        .then(function (results) {
          if (!(product && typeof product === 'object')) return;
          var batchOk = results[0] === true;
          var store = window.AOneProductWeights;
          var inStore =
            !!(store && store.products && typeof store.products.has === 'function') &&
            ids.every(function (id) {
              return store.products.has(id);
            });
          // Leave Checking... until every product in the linked family has batch data.
          product.__pdpProductsBatchResolved = batchOk || inStore;
        })
        .finally(function () {
          productLoadPromiseById.delete(requestKey);
        });
      productLoadPromiseById.set(requestKey, req);
      return req;
    }

    function shopifyImageUrl(src, width) {
      if (!src) return '';
      try {
        var url = new URL(src, window.location.href);
        url.searchParams.set('width', String(width));
        return url.href;
      } catch (e) {
        var q = src.indexOf('?');
        var base = q >= 0 ? src.slice(0, q) : src;
        return base + '?width=' + width;
      }
    }

    function shopifyImageSrcset(src, widths) {
      return widths
        .map(function (w) {
          return shopifyImageUrl(src, w) + ' ' + w + 'w';
        })
        .join(', ');
    }

    function getMediaBaseSrc(media) {
      if (!media) return '';
      if (typeof media.src === 'string' && media.src) return media.src;
      if (media.preview_image && media.preview_image.src) return media.preview_image.src;
      return '';
    }

    function buildGalleryViewerImages(product) {
      var images = [];
      var mediaList = Array.isArray(product && product.media) ? product.media : [];
      for (var i = 0; i < mediaList.length; i += 1) {
        var media = mediaList[i];
        if (!media || typeof media !== 'object') continue;
        var src = getMediaBaseSrc(media);
        if (!src) continue;
        images.push({
          src: shopifyImageUrl(src, 1600),
          alt: media.alt || '',
        });
      }
      return images;
    }

    function renderMainImage(main, opts) {
      var img = document.createElement('img');
      img.className = 'pdp-media__img plp-exp-media__img';
      img.setAttribute('data-plp-exp-main-img', '');
      img.loading = opts.eager ? 'eager' : 'lazy';
      if (opts.eager) img.setAttribute('fetchpriority', 'high');
      img.sizes = opts.sizes || PDP_MAIN_SIZES;
      img.alt = opts.alt || '';
      img.decoding = 'async';
      var baseSrc = opts.baseSrc || '';
      if (baseSrc) {
        img.src = opts.src || shopifyImageUrl(baseSrc, 1600);
        img.srcset = opts.srcset || shopifyImageSrcset(baseSrc, PDP_MAIN_WIDTHS);
      }
      if (img.complete) img.classList.add('pdp-media__img--loaded');
      else img.addEventListener('load', function () {
        img.classList.add('pdp-media__img--loaded');
      }, { once: true });
      main.innerHTML = '';
      main.appendChild(img);
    }

    function renderMainMedia(main, media, thumb, options) {
      options = options || {};
      if (!media) return;
      if (media.media_type === 'video' || media.media_type === 'external_video') {
        main.innerHTML = '';
        var vid = document.createElement('video');
        vid.className = 'pdp-media__video';
        vid.controls = true;
        vid.playsInline = true;
        if (media.sources && media.sources.length) {
          media.sources.forEach(function (s) {
            var source = document.createElement('source');
            source.src = s.url;
            source.type = s.mime_type || 'video/mp4';
            vid.appendChild(source);
          });
        }
        main.appendChild(vid);
        return;
      }
      var baseSrc = (thumb && thumb.getAttribute('data-pdp-full-src')) || getMediaBaseSrc(media);
      renderMainImage(main, {
        baseSrc: baseSrc,
        src: thumb && thumb.getAttribute('data-pdp-full-src'),
        srcset: thumb && thumb.getAttribute('data-pdp-full-srcset'),
        sizes: (thumb && thumb.getAttribute('data-pdp-full-sizes')) || PDP_MAIN_SIZES,
        alt: (thumb && thumb.getAttribute('data-pdp-alt')) || (media.alt && media.alt) || '',
        eager: !!options.eager,
      });
    }

    function initGallery(root, product) {
      var main = root.querySelector('[data-pdp-main-media]');
      var thumbs = root.querySelectorAll('[data-pdp-thumb]');
      var viewImagesBtn = root.querySelector('[data-plp-view-images]');
      var galleryRoot = root.querySelector('[data-plp-exp-media]');
      var isCompactGallery =
        galleryRoot instanceof HTMLElement &&
        (galleryRoot.getAttribute('data-pdp-gallery-layout') === 'horizontal' ||
          galleryRoot.getAttribute('data-pdp-gallery-layout') === 'vertical-rail');
      if ((!main || !thumbs.length) && !(viewImagesBtn instanceof HTMLButtonElement)) return;

      function openViewerAt(index, trigger) {
        var viewer = window.plpVariantDetails;
        if (!viewer || typeof viewer.openImageViewer !== 'function') return;
        var images = buildGalleryViewerImages(product);
        if (!images.length) return;
        var safeIndex = Number.isFinite(index) && index >= 0 ? index : 0;
        viewer.openImageViewer(images, safeIndex, trigger || viewImagesBtn || thumbs[0]);
      }

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var idx = Number(thumb.getAttribute('data-pdp-thumb'));
          if (!Number.isFinite(idx)) return;
          thumbs.forEach(function (t) {
            var active = Number(t.getAttribute('data-pdp-thumb')) === idx;
            t.classList.toggle('is-active', active);
            t.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          var media = product.media && product.media[idx];
          if (media) renderMainMedia(main, media, thumb, { eager: false });
          if (isCompactGallery) openViewerAt(idx, thumb);
        });
      });

      if (viewImagesBtn instanceof HTMLButtonElement) {
        viewImagesBtn.addEventListener('click', function (event) {
          event.preventDefault();
          var activeThumb = root.querySelector('[data-pdp-thumb].is-active');
          var activeIndex =
            activeThumb instanceof HTMLElement
              ? Number(activeThumb.getAttribute('data-pdp-thumb') || '0')
              : 0;
          if (!Number.isFinite(activeIndex) || activeIndex < 0) activeIndex = 0;
          openViewerAt(activeIndex, viewImagesBtn);
        });
      }
    }

    function syncMergedChainAvailabilityBadge(root, product) {
      var badge = root.querySelector('[data-merged-chain-availability-badge]');
      if (!(badge instanceof HTMLElement)) return;

      // Product-level AVAILABLE / COMING SOON from Shopify inventory totals.
      var quantity = 0;
      (product && Array.isArray(product.variants) ? product.variants : []).forEach(function (variant) {
        quantity += resolveVariantShopifyQty(variant);
      });

      if (quantity > 0) {
        badge.textContent = 'AVAILABLE';
        badge.classList.remove('is-coming-soon');
      } else {
        badge.textContent = 'COMING SOON';
        badge.classList.add('is-coming-soon');
      }
    }

    function initAccordion(root) {
      root.querySelectorAll('[data-pdp-acc-trigger]').forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          var item = trigger.closest('[data-pdp-acc-item]');
          if (!item) return;
          var willOpen = !item.classList.contains('is-open');
          root.querySelectorAll('[data-pdp-acc-item]').forEach(function (i) {
            i.classList.remove('is-open');
          });
          if (willOpen) item.classList.add('is-open');
        });
      });
    }

    function setStatus(root, message, isError) {
      var status = root.querySelector('[data-premium-pdp-status]');
      if (!(status instanceof HTMLElement)) return;
      status.textContent = message || '';
      status.classList.toggle('is-error', !!isError);
    }

    function parseOptionDefinitions(root) {
      var el = root.querySelector('[data-premium-pdp-option-definitions]');
      if (!el || !el.textContent) return [];
      try {
        var parsed = JSON.parse(el.textContent);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
      } catch (e) {
        return [];
      }
    }

    function normalizeKey(value) {
      return String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ');
    }

    function isMaterialAxisKey(key) {
      var normalized = normalizeKey(key);
      return /(material|karat|carat|metal|purity|jewellery|jewelry)/.test(normalized);
    }

    function isWidthAxisKey(key) {
      var normalized = normalizeKey(key);
      if (isMaterialAxisKey(normalized)) return false;
      return /(width|thickness|gauge|\bmm\b|diameter|\bdia\b)/.test(normalized);
    }

    function isLengthAxisKey(key) {
      return /(length|inch|inches|\blen\b)/.test(normalizeKey(key));
    }

    function isKaratLikeValue(value) {
      return /^\d{1,2}\s*k(?:t)?$/i.test(String(value == null ? '' : value).trim());
    }

    function normalizeWidthLabel(value) {
      var raw = String(value == null ? '' : value).trim();
      if (!raw || raw === '—') return raw || '—';
      if (isKaratLikeValue(raw)) return raw;
      var mmMatch = raw.match(/^(\d+(?:\.\d+)?)\s*mm$/i);
      if (mmMatch) return mmMatch[1];
      return raw;
    }

    function extractWidthFromProduct(product) {
      if (!product || typeof product !== 'object') return '';
      var texts = [];
      if (product.title) texts.push(String(product.title));
      if (product.handle) texts.push(String(product.handle).replace(/[-_]+/g, ' '));
      if (Array.isArray(product.tags)) {
        product.tags.forEach(function (tag) {
          if (tag != null && String(tag).trim()) texts.push(String(tag));
        });
      }
      for (var i = 0; i < texts.length; i += 1) {
        var match = texts[i].match(/(\d+(?:\.\d+)?)\s*mm\b/i);
        if (match) return normalizeWidthLabel(match[1]);
      }
      return '';
    }

    function escapeHtml(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    function escapeAttr(value) {
      return escapeHtml(value).replace(/"/g, '&quot;');
    }

    function getVariantOptionValueByPosition(variant, position) {
      var idx = Number(position) - 1;
      if (!Number.isFinite(idx) || idx < 0) return '';
      var direct = variant['option' + String(idx + 1)];
      if (direct != null && String(direct).trim()) return String(direct).trim();
      var opts = Array.isArray(variant.options) ? variant.options : [];
      return opts[idx] != null ? String(opts[idx]).trim() : '';
    }

    function extractNumericValue(raw) {
      var match = String(raw || '').match(/-?\d+(\.\d+)?/);
      if (!match) return null;
      var parsed = Number(match[0]);
      return Number.isFinite(parsed) ? parsed : null;
    }

    function sortMatrixValues(values) {
      return values.slice().sort(function (a, b) {
        var aNum = extractNumericValue(a);
        var bNum = extractNumericValue(b);
        if (aNum != null && bNum != null && aNum !== bNum) return aNum - bNum;
        return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
      });
    }

    function detectMatrixConfig(optionDefs, product) {
      if (!Array.isArray(optionDefs)) return null;
      if (optionDefs.length < 1 || optionDefs.length > 3) return null;

      var widthDef = null;
      var lengthDef = null;
      optionDefs.forEach(function (def) {
        var key = normalizeKey(def && (def.key || def.name || ''));
        if (!widthDef && isWidthAxisKey(key)) widthDef = def;
        if (!lengthDef && isLengthAxisKey(key)) lengthDef = def;
      });

      if (!lengthDef) {
        lengthDef =
          optionDefs.find(function (def) {
            var key = normalizeKey(def && (def.key || def.name || ''));
            return def !== widthDef && !isMaterialAxisKey(key);
          }) || null;
      }

      var rowDef = widthDef;
      var fixedWidth = '';
      if (!rowDef) {
        fixedWidth = extractWidthFromProduct(product || {});
        if (fixedWidth) {
          rowDef = {
            name: 'Width',
            key: 'width',
            displayLabel: 'WIDTH (MM)',
            position: null,
            fixedValue: fixedWidth,
          };
        }
      }

      if (!rowDef) {
        rowDef = optionDefs.find(function (def) {
          var key = normalizeKey(def && (def.key || def.name || ''));
          return def !== lengthDef && !isMaterialAxisKey(key);
        }) || null;
      }

      var colDef =
        lengthDef ||
        optionDefs.find(function (d) {
          return d !== rowDef && !isMaterialAxisKey(normalizeKey(d && (d.key || d.name || '')));
        }) ||
        null;

      if (!rowDef || !colDef || rowDef === colDef) return null;

      var contextDefs = optionDefs.filter(function (def) {
        return def !== rowDef && def !== colDef;
      });
      if (contextDefs.length > 1) return null;

      return {
        rowDef: rowDef,
        colDef: colDef,
        contextDef: contextDefs.length ? contextDefs[0] : null,
      };
    }

    function chooseMatrixVariant(current, next) {
      // Keep first mapped variant stable. Do not prefer Shopify variant.available.
      if (!current) return next;
      return current;
    }

    function getMatrixStockStatus(wmsSkuDataOrQty) {
      var inventoryApi = window.WMSInventory || window.AOneWmsAvailability;
      var rawQty = 0;
      var effectiveQty = 0;
      var minWeight = null;

      if (wmsSkuDataOrQty && typeof wmsSkuDataOrQty === 'object') {
        effectiveQty = resolveMaxSelectableQuantity(wmsSkuDataOrQty);
        rawQty = Math.max(
          0,
          Math.floor(
            Number(
              wmsSkuDataOrQty.rawAvailableCount != null
                ? wmsSkuDataOrQty.rawAvailableCount
                : wmsSkuDataOrQty.count != null
                  ? wmsSkuDataOrQty.count
                  : effectiveQty
            ) || 0
          )
        );
        if (wmsSkuDataOrQty.found === false) rawQty = 0;
        minWeight = wmsSkuDataOrQty.minWeight;
      } else {
        effectiveQty = Number.isFinite(Number(wmsSkuDataOrQty))
          ? Math.max(0, Math.floor(Number(wmsSkuDataOrQty)))
          : 0;
        rawQty = effectiveQty;
      }

      if (inventoryApi && typeof inventoryApi.resolveVariantAvailabilityState === 'function') {
        var cartReady =
          typeof inventoryApi.isCartStateReady === 'function' ? inventoryApi.isCartStateReady() : true;
        var resolved = inventoryApi.resolveVariantAvailabilityState({
          rawWmsQty: rawQty,
          effectiveQty: effectiveQty,
          effectiveMinWeight: minWeight,
          isLoading: false,
          wmsResolved: true,
          cartResolved: cartReady,
        });

        // WMS-only: if we have qty but no real minimum weight, treat as coming-soon.
        if (resolved.state === 'available' && (!resolved.minimumWeight || !(Number(resolved.minimumWeight) > 0))) {
          return { key: 'coming-soon', label: 'COMING SOON', quantity: 0, minimumWeightLabel: 'Min wt: N/A' };
        }
        return {
          key: resolved.state,
          label: resolved.label,
          quantity:
            resolved.state === 'all-in-cart'
              ? resolved.availableQuantity != null
                ? resolved.availableQuantity
                : 0
              : resolved.availableQuantity,
          remainingQuantity: resolved.availableQuantity,
          minimumWeightLabel: resolved.minimumWeightLabel,
        };
      }

      var statusKey =
        inventoryApi && typeof inventoryApi.getStorefrontStatus === 'function'
          ? inventoryApi.getStorefrontStatus(rawQty, effectiveQty)
          : effectiveQty > 0
            ? 'AVAILABLE'
            : rawQty > 0
              ? 'ALL_IN_CART'
              : 'COMING_SOON';

      if (statusKey === 'ALL_IN_CART') {
        return {
          key: 'all-in-cart',
          label: 'IN-STOCK: 0',
          quantity: 0,
          remainingQuantity: 0,
          minimumWeightLabel: 'Min wt: N/A',
        };
      }
      if (statusKey === 'COMING_SOON' || (effectiveQty <= 0 && rawQty <= 0)) {
        return { key: 'coming-soon', label: 'COMING SOON', quantity: 0, minimumWeightLabel: 'Min wt: N/A' };
      }
      if (minWeight == null || Number(minWeight) <= 0) {
        return { key: 'coming-soon', label: 'COMING SOON', quantity: 0, minimumWeightLabel: 'Min wt: N/A' };
      }
      return {
        key: 'available',
        label: 'IN-STOCK: ' + effectiveQty,
        quantity: effectiveQty,
        remainingQuantity: effectiveQty,
        minimumWeightLabel:
          inventoryApi && typeof inventoryApi.formatMinimumWeightLabel === 'function'
            ? inventoryApi.formatMinimumWeightLabel(minWeight)
            : minWeight != null && Number(minWeight) > 0
              ? 'Min wt: ' + formatWeight(minWeight)
              : 'Min wt: N/A',
      };
    }

    function getMatrixCompactStatusText(status) {
      if (!status) return 'Coming Soon';
      if (status.key === 'available') return 'AVAILABLE: ' + status.quantity;
      if (status.key === 'all-in-cart') return 'QTY: ' + status.quantity;
      if (status.key === 'coming-soon' || status.key === 'error') return 'Coming Soon';
      return 'Coming Soon';
    }

    function getInitialLayoutMode(root, optionDefs, product) {
      var layout = normalizeKey(root && root.dataset && root.dataset.variantLayout);
      if (layout === 'matrix' || layout === 'table') return layout;
      var detected = detectMatrixConfig(optionDefs, product);
      return detected ? 'matrix' : 'table';
    }

    function resolveMatrixAxisValue(variant, axisDef) {
      if (!axisDef) return '';
      if (axisDef.fixedValue != null && String(axisDef.fixedValue).trim()) {
        return normalizeWidthLabel(axisDef.fixedValue);
      }
      var raw = getVariantOptionValueByPosition(variant, axisDef.position) || '—';
      if (normalizeKey(axisDef.key || axisDef.name || '') === 'width' || isWidthAxisKey(axisDef.key || axisDef.name || '')) {
        return normalizeWidthLabel(raw);
      }
      return raw;
    }

    function buildMatrixData(product, matrixConfig) {
      var rowDef = matrixConfig.rowDef;
      var colDef = matrixConfig.colDef;
      var contextDef = matrixConfig.contextDef;
      var rowSet = new Set();
      var colSet = new Set();
      var contextSet = new Set();
      var map = new Map();

      (product.variants || []).forEach(function (variant) {
        if (!variant || typeof variant !== 'object') return;
        var rowVal = resolveMatrixAxisValue(variant, rowDef) || '—';
        var colVal = getVariantOptionValueByPosition(variant, colDef.position) || '—';
        var contextVal = contextDef ? (getVariantOptionValueByPosition(variant, contextDef.position) || '—') : '';
        rowSet.add(rowVal);
        colSet.add(colVal);
        if (contextDef) contextSet.add(contextVal);
        var key = [contextVal, rowVal, colVal].join('||');
        map.set(key, chooseMatrixVariant(map.get(key), variant));
      });

      return {
        rowValues: sortMatrixValues(Array.from(rowSet)),
        colValues: sortMatrixValues(Array.from(colSet)),
        contextValues: contextDef ? sortMatrixValues(Array.from(contextSet)) : [''],
        map: map,
        rowDef: rowDef,
        colDef: colDef,
        contextDef: contextDef,
      };
    }

    function getMatrixMaterialValue(variant, matrixData) {
      var defs = [matrixData.rowDef, matrixData.colDef, matrixData.contextDef].filter(Boolean);
      for (var i = 0; i < defs.length; i += 1) {
        var def = defs[i];
        if (normalizeKey(def.key || def.name) !== 'material') continue;
        return getVariantOptionValueByPosition(variant, def.position) || '';
      }
      return '';
    }

    function getCachedWmsBySku(product) {
      var skuMap = new Map();
      (product.variants || []).forEach(function (variant) {
        var displaySku = normalizeSkuDisplay(variant && variant.sku);
        var key = normalizeSku(displaySku);
        if (!key) return;
        var node = getCachedNodeForSku(
          { id: variant.product_id || variant.productId || (product && product.id) },
          displaySku
        );
        if (!node) return;
        if (node.__pdpInventoryPending) {
          skuMap.set(key, { sku: displaySku, pending: true });
          return;
        }
        skuMap.set(key, normalizePdpWmsSkuData(node, displaySku));
      });
      return skuMap;
    }

    function resolveVariantMinWeight(variant, wmsNode) {
      var fromWms = extractMinWeightFromNode(wmsNode);
      if (fromWms != null && fromWms > 0) return fromWms;
      var base = Number(variant && variant.weight);
      if (!Number.isFinite(base) || base <= 0) return null;
      var unit = normalizeKey(variant && variant.weight_unit);
      return unit === 'kg' ? base * 1000 : base;
    }

    function getAxisLabel(def) {
      var label = String(def.displayLabel || def.name || '').trim();
      var original = String(def.name || '').trim();
      var key = normalizeKey(def.key || def.name || '');
      if (key === 'width') {
        return 'WIDTH (MM)';
      }
      if (key === 'length') {
        return 'LENGTH (INCHES)';
      }
      return label || original.toUpperCase();
    }

    function normalizeContextValue(value) {
      return String(value == null ? '' : value)
        .trim()
        .replace(/\s+/g, ' ');
    }

    function getMeaningfulContextValues(values) {
      var list = Array.isArray(values) ? values : [];
      var seen = Object.create(null);
      var output = [];
      for (var i = 0; i < list.length; i += 1) {
        var raw = normalizeContextValue(list[i]);
        if (!raw || raw === '—' || raw === '-') continue;
        var key = raw.toLowerCase();
        if (seen[key]) continue;
        seen[key] = true;
        output.push(raw);
      }
      return output;
    }

    function resetSummary(detailsRoot) {
      detailsRoot.querySelectorAll('[data-details-selected-count]').forEach(function (el) {
        el.textContent = '0';
      });
      detailsRoot.querySelectorAll('[data-details-total-qty]').forEach(function (el) {
        el.textContent = '0';
      });
      detailsRoot.querySelectorAll('[data-details-total-weight]').forEach(function (el) {
        el.textContent = '0.00g';
      });
      var addBtn = detailsRoot.querySelector('[data-details-add-selected]');
      if (addBtn instanceof HTMLButtonElement) addBtn.disabled = true;
    }

    /**
     * After matrix/list paint: hydrate draft from Shopify cart and refresh footer.
     * Do NOT call resetSummary here — that wiped cart-restored totals (Bug 1 on PDP).
     * @param {HTMLElement} detailsRoot
     * @param {{ force?: boolean, isLoading?: boolean }} [options]
     */
    function syncPdpDraftFromCart(detailsRoot, options) {
      options = options || {};
      if (!(detailsRoot instanceof HTMLElement)) return;
      if (options.isLoading === true) {
        resetSummary(detailsRoot);
        return;
      }
      var plp = window.plpVariantDetails;
      if (plp && typeof plp.hydrateCartDraftSelections === 'function') {
        plp.hydrateCartDraftSelections(detailsRoot, { force: Boolean(options.force) });
      } else if (plp && typeof plp.applyCartSelectionToRow === 'function') {
        detailsRoot.querySelectorAll('[data-details-variant-row]').forEach(function (row) {
          if (!(row instanceof HTMLElement)) return;
          plp.applyCartSelectionToRow(row, { force: Boolean(options.force) });
          syncRowControls(row);
        });
        if (typeof plp.syncDraftSummary === 'function') plp.syncDraftSummary(detailsRoot);
      } else {
        detailsRoot.querySelectorAll('[data-details-variant-row]').forEach(function (row) {
          if (row instanceof HTMLElement) syncRowControls(row);
        });
      }
      // Ensure qty/weight controls respect cart baseline after HTML rebuild.
      detailsRoot.querySelectorAll('[data-details-variant-row]').forEach(function (row) {
        if (row instanceof HTMLElement) syncRowControls(row);
      });
    }

    function clearBulkSelections(detailsRoot) {
      var host = detailsRoot.closest('.plp-view-host');
      if (!(host instanceof HTMLElement)) return;
      if (!window.plpBulkSelection || typeof window.plpBulkSelection.clearProductVariants !== 'function') return;
      window.plpBulkSelection.clearProductVariants(host, detailsRoot);
    }

    function syncRowControls(row) {
      var input = row.querySelector('[data-details-qty-input]');
      var minus = row.querySelector('[data-details-qty-minus]');
      var plus = row.querySelector('[data-details-qty-plus]');
      var weightBtn = row.querySelector('[data-details-weight-open]');
      var available = row.dataset.variantAvailable === 'true';
      var remaining = Number(row.dataset.variantInventoryQty || 0);
      var cartBaseline = Number(row.dataset.cartBaselineQty || 0);
      if (!Number.isFinite(cartBaseline) || cartBaseline < 0) cartBaseline = 0;
      var absoluteMax = Math.max(0, remaining) + cartBaseline;
      var qty = input instanceof HTMLInputElement ? Math.max(0, parseInt(input.value || '0', 10) || 0) : 0;
      var canInteract =
        available || cartBaseline > 0 || String(row.dataset.wmsStatus || '') === 'all-in-cart';
      if (input instanceof HTMLInputElement) {
        input.disabled = !canInteract;
        input.max = String(absoluteMax);
        if (absoluteMax >= 0 && qty > absoluteMax) input.value = String(absoluteMax);
        if (!canInteract) input.value = '0';
        qty = Math.max(0, parseInt(input.value || '0', 10) || 0);
      }
      if (minus instanceof HTMLButtonElement) minus.disabled = !canInteract || qty <= 0;
      if (plus instanceof HTMLButtonElement) plus.disabled = !canInteract || qty >= absoluteMax;
      if (weightBtn instanceof HTMLButtonElement) weightBtn.disabled = !canInteract;
    }

    function setActiveSku(root, sku) {
      var skuEl = root.querySelector('[data-premium-pdp-active-sku]');
      if (!(skuEl instanceof HTMLElement)) return;
      var displaySku = normalizeSkuDisplay(sku);
      var canCopy = Boolean(displaySku);
      skuEl.textContent = displaySku || '—';
      var trigger = skuEl.closest('[data-premium-pdp-sku-tip]');
      if (!(trigger instanceof HTMLElement)) return;
      trigger.setAttribute('data-tip-sku', displaySku);
      trigger.setAttribute(
        'aria-label',
        canCopy ? 'SKU ' + displaySku + '. Show copy options' : 'SKU unavailable'
      );
      if (trigger instanceof HTMLButtonElement) trigger.disabled = !canCopy;
    }

    /** Shared PDP SKU copy tooltip (header SKU + matrix info tip hover/focus). */
    var pdpSkuTooltipState = {
      tip: null,
      activeTrigger: null,
      hideTimer: null,
      copyResetTimer: null,
      shownAt: 0,
    };

    function cancelPdpSkuTooltipHide() {
      if (pdpSkuTooltipState.hideTimer != null) {
        clearTimeout(pdpSkuTooltipState.hideTimer);
        pdpSkuTooltipState.hideTimer = null;
      }
    }

    function schedulePdpSkuTooltipHide() {
      cancelPdpSkuTooltipHide();
      pdpSkuTooltipState.hideTimer = setTimeout(function () {
        pdpSkuTooltipState.hideTimer = null;
        var tip = pdpSkuTooltipState.tip;
        var active = document.activeElement;
        if (tip instanceof HTMLElement && active instanceof Node && tip.contains(active)) return;
        hidePdpSkuTooltip();
      }, 180);
    }

    function ensurePdpSkuTooltip() {
      if (pdpSkuTooltipState.tip instanceof HTMLElement) return pdpSkuTooltipState.tip;
      var tip = document.createElement('div');
      tip.className = 'premium-pdp__sku-tooltip';
      tip.id = 'PremiumPdpSkuTooltip';
      tip.setAttribute('role', 'dialog');
      tip.setAttribute('aria-label', 'Copy SKU');
      tip.hidden = true;
      tip.addEventListener('pointerenter', cancelPdpSkuTooltipHide);
      tip.addEventListener('pointerleave', function (e) {
        var related = e.relatedTarget;
        if (
          related instanceof Node &&
          ((pdpSkuTooltipState.activeTrigger && pdpSkuTooltipState.activeTrigger.contains(related)) ||
            tip.contains(related))
        ) {
          return;
        }
        schedulePdpSkuTooltipHide();
      });
      tip.addEventListener('focusin', cancelPdpSkuTooltipHide);
      tip.addEventListener('focusout', function (e) {
        var related = e.relatedTarget;
        if (
          related instanceof Node &&
          ((pdpSkuTooltipState.activeTrigger && pdpSkuTooltipState.activeTrigger.contains(related)) ||
            tip.contains(related))
        ) {
          return;
        }
        schedulePdpSkuTooltipHide();
      });
      tip.addEventListener('click', function (e) {
        var copyBtn = e.target instanceof Element ? e.target.closest('[data-pdp-sku-copy]') : null;
        if (!(copyBtn instanceof HTMLButtonElement) || !tip.contains(copyBtn)) return;
        e.preventDefault();
        e.stopPropagation();
        copyPdpSkuFromTooltip(copyBtn);
      });
      document.body.appendChild(tip);
      pdpSkuTooltipState.tip = tip;
      return tip;
    }

    function buildPdpSkuTooltipHtml(sku) {
      var safeSku = normalizeSkuDisplay(sku) || '—';
      var canCopy = safeSku !== '—';
      return (
        '<div class="premium-pdp__sku-tooltip-row">' +
        '<span class="premium-pdp__sku-tooltip-value">SKU: ' +
        escapeHtml(safeSku) +
        '</span>' +
        (canCopy
          ? '<button type="button" class="premium-pdp__sku-tooltip-copy" data-pdp-sku-copy data-sku="' +
            escapeAttr(safeSku) +
            '" aria-label="Copy SKU ' +
            escapeAttr(safeSku) +
            '">Copy</button>'
          : '') +
        '</div>'
      );
    }

    function positionPdpSkuTooltip(trigger) {
      var tip = ensurePdpSkuTooltip();
      if (tip.hidden || !(trigger instanceof HTMLElement)) return;
      var rect = trigger.getBoundingClientRect();
      var tipRect = tip.getBoundingClientRect();
      var margin = 8;
      var gap = 10;
      var placeBelow = false;
      var top = rect.top - tipRect.height - gap;
      var left = rect.left + rect.width / 2 - tipRect.width / 2;

      if (left < margin) left = margin;
      if (left + tipRect.width > window.innerWidth - margin) {
        left = Math.max(margin, window.innerWidth - tipRect.width - margin);
      }
      if (top < margin) {
        top = rect.bottom + gap;
        placeBelow = true;
      }
      if (top + tipRect.height > window.innerHeight - margin) {
        top = Math.max(margin, window.innerHeight - tipRect.height - margin);
        placeBelow = top > rect.bottom;
      }

      var caretX = rect.left + rect.width / 2 - left;
      caretX = Math.max(12, Math.min(tipRect.width - 12, caretX));

      tip.classList.toggle('is-below', placeBelow);
      tip.style.setProperty('--sku-tip-caret-x', Math.round(caretX) + 'px');
      tip.style.top = Math.round(top) + 'px';
      tip.style.left = Math.round(left) + 'px';
    }

    function showPdpSkuTooltip(trigger) {
      if (!(trigger instanceof HTMLElement) || trigger.disabled) return;
      var sku = normalizeSkuDisplay(trigger.getAttribute('data-tip-sku'));
      if (!sku) return;
      cancelPdpSkuTooltipHide();
      if (pdpSkuTooltipState.activeTrigger && pdpSkuTooltipState.activeTrigger !== trigger) {
        pdpSkuTooltipState.activeTrigger.setAttribute('aria-expanded', 'false');
        pdpSkuTooltipState.activeTrigger.removeAttribute('aria-describedby');
      }
      var tip = ensurePdpSkuTooltip();
      tip.innerHTML = buildPdpSkuTooltipHtml(sku);
      tip.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      trigger.setAttribute('aria-describedby', tip.id);
      pdpSkuTooltipState.activeTrigger = trigger;
      pdpSkuTooltipState.shownAt = Date.now();
      requestAnimationFrame(function () {
        positionPdpSkuTooltip(trigger);
      });
    }

    function hidePdpSkuTooltip() {
      cancelPdpSkuTooltipHide();
      if (pdpSkuTooltipState.copyResetTimer != null) {
        clearTimeout(pdpSkuTooltipState.copyResetTimer);
        pdpSkuTooltipState.copyResetTimer = null;
      }
      if (pdpSkuTooltipState.activeTrigger) {
        pdpSkuTooltipState.activeTrigger.setAttribute('aria-expanded', 'false');
        pdpSkuTooltipState.activeTrigger.removeAttribute('aria-describedby');
        pdpSkuTooltipState.activeTrigger = null;
      }
      if (pdpSkuTooltipState.tip) {
        pdpSkuTooltipState.tip.hidden = true;
        pdpSkuTooltipState.tip.classList.remove('is-below');
        pdpSkuTooltipState.tip.style.removeProperty('--sku-tip-caret-x');
        pdpSkuTooltipState.tip.innerHTML = '';
      }
    }

    function togglePdpSkuTooltip(trigger) {
      var alreadyOpen =
        pdpSkuTooltipState.activeTrigger === trigger &&
        pdpSkuTooltipState.tip instanceof HTMLElement &&
        !pdpSkuTooltipState.tip.hidden;
      if (alreadyOpen && Date.now() - pdpSkuTooltipState.shownAt > 250) {
        hidePdpSkuTooltip();
        return;
      }
      showPdpSkuTooltip(trigger);
    }

    function copyPdpSkuFromTooltip(copyBtn) {
      // Prefer the Copy button's own data-sku (set when this tip was opened for a specific cell),
      // then fall back to the active trigger's data-tip-sku — never a generic .sku query.
      var sku = normalizeSkuDisplay(
        (copyBtn && copyBtn.getAttribute('data-sku')) ||
          (pdpSkuTooltipState.activeTrigger &&
            pdpSkuTooltipState.activeTrigger.getAttribute('data-tip-sku'))
      );
      if (!sku || sku === '—') return;
      cancelPdpSkuTooltipHide();

      var markCopied = function () {
        if (!(copyBtn instanceof HTMLButtonElement) || !copyBtn.isConnected) return;
        copyBtn.textContent = 'Copied';
        copyBtn.classList.add('is-copied');
        if (pdpSkuTooltipState.copyResetTimer != null) clearTimeout(pdpSkuTooltipState.copyResetTimer);
        pdpSkuTooltipState.copyResetTimer = setTimeout(function () {
          pdpSkuTooltipState.copyResetTimer = null;
          if (!copyBtn.isConnected) return;
          copyBtn.textContent = 'Copy';
          copyBtn.classList.remove('is-copied');
        }, 1600);
      };

      var fallbackCopy = function () {
        try {
          var ta = document.createElement('textarea');
          ta.value = sku;
          ta.setAttribute('readonly', '');
          ta.style.position = 'fixed';
          ta.style.top = '0';
          ta.style.left = '0';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          var ok = document.execCommand && document.execCommand('copy');
          document.body.removeChild(ta);
          if (ok) {
            markCopied();
            return;
          }
        } catch (e) {
          /* fall through to select-only */
        }
        var valueEl =
          pdpSkuTooltipState.tip && pdpSkuTooltipState.tip.querySelector('.premium-pdp__sku-tooltip-value');
        if (!(valueEl instanceof HTMLElement)) return;
        var range = document.createRange();
        range.selectNodeContents(valueEl);
        var selection = window.getSelection();
        if (!selection) return;
        selection.removeAllRanges();
        selection.addRange(range);
      };

      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        navigator.clipboard.writeText(sku).then(markCopied).catch(fallbackCopy);
        return;
      }
      fallbackCopy();
    }

    function bindPdpSkuCopyTooltips() {
      if (document.documentElement.dataset.aonePdpSkuTipsBound === 'true') return;
      document.documentElement.dataset.aonePdpSkuTipsBound = 'true';

      document.addEventListener(
        'pointerover',
        function (e) {
          var trigger = e.target instanceof Element ? e.target.closest('[data-premium-pdp-sku-tip]') : null;
          if (!(trigger instanceof HTMLElement)) return;
          var related = e.relatedTarget;
          if (related instanceof Node && trigger.contains(related)) return;
          showPdpSkuTooltip(trigger);
        },
        true
      );

      document.addEventListener(
        'pointerout',
        function (e) {
          var trigger = e.target instanceof Element ? e.target.closest('[data-premium-pdp-sku-tip]') : null;
          if (!(trigger instanceof HTMLElement)) return;
          var related = e.relatedTarget;
          if (
            related instanceof Node &&
            (trigger.contains(related) ||
              (pdpSkuTooltipState.tip && pdpSkuTooltipState.tip.contains(related)))
          ) {
            return;
          }
          if (document.activeElement === trigger) return;
          schedulePdpSkuTooltipHide();
        },
        true
      );

      document.addEventListener(
        'focusin',
        function (e) {
          var trigger = e.target instanceof Element ? e.target.closest('[data-premium-pdp-sku-tip]') : null;
          if (trigger instanceof HTMLElement) showPdpSkuTooltip(trigger);
        },
        true
      );

      document.addEventListener(
        'focusout',
        function (e) {
          var trigger = e.target instanceof Element ? e.target.closest('[data-premium-pdp-sku-tip]') : null;
          if (!(trigger instanceof HTMLElement)) return;
          var related = e.relatedTarget;
          if (
            related instanceof Node &&
            (trigger.contains(related) ||
              (pdpSkuTooltipState.tip && pdpSkuTooltipState.tip.contains(related)))
          ) {
            return;
          }
          schedulePdpSkuTooltipHide();
        },
        true
      );

      document.addEventListener(
        'click',
        function (e) {
          var trigger = e.target instanceof Element ? e.target.closest('[data-premium-pdp-sku-tip]') : null;
          if (trigger instanceof HTMLElement) {
            e.preventDefault();
            e.stopPropagation();
            togglePdpSkuTooltip(trigger);
            return;
          }
          if (
            pdpSkuTooltipState.tip instanceof HTMLElement &&
            !pdpSkuTooltipState.tip.hidden &&
            e.target instanceof Node &&
            !pdpSkuTooltipState.tip.contains(e.target)
          ) {
            hidePdpSkuTooltip();
          }
        },
        true
      );

      document.addEventListener(
        'keydown',
        function (e) {
          if (e.key !== 'Escape') return;
          if (!pdpSkuTooltipState.tip || pdpSkuTooltipState.tip.hidden) return;
          e.preventDefault();
          var trigger = pdpSkuTooltipState.activeTrigger;
          hidePdpSkuTooltip();
          if (trigger instanceof HTMLElement) trigger.focus();
        },
        true
      );

      document.addEventListener(
        'scroll',
        function () {
          if (pdpSkuTooltipState.tip && !pdpSkuTooltipState.tip.hidden) hidePdpSkuTooltip();
        },
        true
      );

      window.addEventListener('resize', function () {
        if (pdpSkuTooltipState.tip && !pdpSkuTooltipState.tip.hidden) hidePdpSkuTooltip();
      });
    }

    function isMatrixMobileViewport() {
      // Always keep the desktop matrix table. Mobile/tablet use CSS horizontal scroll
      // (.premium-pdp-matrix__scroll) instead of the card layout swap.
      return false;
    }

    /** @type {WeakMap<HTMLElement, any>} */
    var matrixRenderStateByRoot = new WeakMap();

    function bindMatrixViewportListener(root) {
      // No-op: do not re-render on viewport changes (would reset qty/weight state).
      // Layout is handled entirely by CSS horizontal scrolling.
      if (!(root instanceof HTMLElement)) return;
      root.dataset.matrixViewportBound = 'true';
    }

    /**
     * Measure the Length group header so the inch-value row sticks directly under it.
     * @param {HTMLElement} scrollEl
     */
    function syncMatrixStickyHeaderHeights(scrollEl) {
      if (!(scrollEl instanceof HTMLElement)) return;
      var groupHeader = scrollEl.querySelector('.premium-pdp-matrix__axis-group');
      var subHeader = scrollEl.querySelector('.premium-pdp-matrix__axis-column');
      if (groupHeader instanceof HTMLElement) {
        var mainHeight = Math.ceil(groupHeader.getBoundingClientRect().height);
        if (mainHeight >= 24 && mainHeight <= 80) {
          scrollEl.style.setProperty('--matrix-main-header-height', mainHeight + 'px');
        } else {
          scrollEl.style.removeProperty('--matrix-main-header-height');
        }
      }
      if (subHeader instanceof HTMLElement) {
        var subHeight = Math.ceil(subHeader.getBoundingClientRect().height);
        if (subHeight >= 24 && subHeight <= 80) {
          scrollEl.style.setProperty('--matrix-sub-header-height', subHeight + 'px');
        } else {
          scrollEl.style.removeProperty('--matrix-sub-header-height');
        }
      }
    }

    /**
     * @param {HTMLElement} matrixRoot
     */
    function bindMatrixStickyHeaderSync(matrixRoot) {
      if (!(matrixRoot instanceof HTMLElement)) return;
      var scroll = matrixRoot.querySelector('.premium-pdp-matrix__scroll');
      if (!(scroll instanceof HTMLElement)) return;
      syncMatrixStickyHeaderHeights(scroll);
      if (scroll.dataset.matrixStickyBound === 'true') return;
      scroll.dataset.matrixStickyBound = 'true';
      if (typeof ResizeObserver === 'function') {
        var ro = new ResizeObserver(function () {
          syncMatrixStickyHeaderHeights(scroll);
        });
        ro.observe(scroll);
        var table = scroll.querySelector('.premium-pdp-matrix__table');
        if (table instanceof HTMLElement) ro.observe(table);
      }
    }

    function buildMatrixCellModel(product, variant, rowVal, colVal, wmsBySku, options) {
      options = options || {};
      var isLoading = options.isLoading === true;
      var productId = normalizeShopifyId(product && product.id);
      var displaySku = normalizeSkuDisplay(variant && variant.sku);
      var skuKey = normalizeSku(displaySku);
      var wmsSkuData = !isLoading && skuKey ? wmsBySku.get(skuKey) : null;
      if (!isLoading && skuKey && !wmsSkuData) {
        var cachedNode = getCachedNodeForSku(
          { id: variant.product_id || variant.productId || (product && product.id) },
          displaySku
        );
        if (cachedNode && cachedNode.__pdpInventoryPending) {
          wmsSkuData = { sku: displaySku, pending: true };
        } else {
          wmsSkuData = normalizePdpWmsSkuData(cachedNode, displaySku);
        }
      }
      var rowPending = Boolean(wmsSkuData && wmsSkuData.pending);
      if (!isLoading && !rowPending && skuKey && (!wmsSkuData || wmsSkuData.found === false)) {
        wmsSkuData = normalizePdpWmsSkuData(
          {
            found: false,
            count: 0,
            rawAvailableCount: 0,
            availableOptions: [],
            storefrontStatus: 'COMING_SOON',
          },
          displaySku
        );
      }
      var availableQty = !isLoading && !rowPending ? resolveMaxSelectableQuantity(wmsSkuData) : 0;
      var wmsStatus =
        isLoading || rowPending ? { key: 'loading' } : getMatrixStockStatus(wmsSkuData || availableQty);
      var shopifyKnown = isVariantShopifyQtyKnown(variant);
      var shopifyQty = shopifyKnown ? resolveVariantShopifyQty(variant) : 0;
      var shopifyDisplay = shopifyKnown
        ? getShopifyDisplayStockStatus(shopifyQty)
        : { key: 'loading', label: '', quantity: 0 };
      var shopifyInStock = shopifyKnown && shopifyQty > 0;
      // Weight / selectable options wait for products-batch. Stock status does not.
      var batchResolved = product && product.__pdpProductsBatchResolved === true;
      var weightLoading =
        isLoading ||
        rowPending ||
        !batchResolved ||
        (wmsStatus && wmsStatus.key === 'loading');
      var showSkeleton = weightLoading;
      var status = {
        key: shopifyDisplay.key,
        label: shopifyDisplay.label,
        quantity: shopifyDisplay.quantity,
        remainingQuantity: shopifyInStock ? shopifyQty : 0,
        minimumWeightLabel: weightLoading ? null : wmsStatus && wmsStatus.minimumWeightLabel,
      };
      var cartQty = 0;
      if (displaySku) {
        var availabilityApi = getWmsApi();
        if (
          availabilityApi &&
          typeof availabilityApi.getCartSkuSummary === 'function' &&
          (typeof availabilityApi.isCartStateReady !== 'function' || availabilityApi.isCartStateReady())
        ) {
          var cartSummary = availabilityApi.getCartSkuSummary(displaySku);
          cartQty = Math.max(0, Math.floor(Number(cartSummary && cartSummary.quantity) || 0));
        }
      }
      var weightActionLabel = weightLoading
        ? 'Checking...'
        : cartQty > 0
          ? 'Edit weight'
          : wmsStatus && wmsStatus.key === 'available' && wmsStatus.minimumWeightLabel && !String(wmsStatus.minimumWeightLabel).includes('N/A')
            ? wmsStatus.minimumWeightLabel
            : shopifyQty > 0
              ? 'Select Weight'
              : 'N/A';
      var materialValue = getMatrixMaterialValue(variant, options.matrixData || {});
      // Quantity follows Shopify. Weight stays disabled while products-batch is loading.
      var rowAvailable = shopifyInStock || cartQty > 0;
      var shopifyOut = shopifyKnown && !shopifyInStock && !(cartQty > 0);
      var cellState = !shopifyKnown ? 'loading' : shopifyInStock || cartQty > 0 ? 'available' : 'coming-soon';
      var wmsStatusAttr = weightLoading
        ? 'loading'
        : shopifyInStock || cartQty > 0
          ? 'available'
          : 'coming-soon';
      var skeletonApi = window.WMSInventory || window.AOneWmsAvailability;
      var statusSkeleton =
        skeletonApi && typeof skeletonApi.getInventorySkeletonMarkup === 'function'
          ? skeletonApi.getInventorySkeletonMarkup('status')
          : '<span class="aone-inventory-skeleton aone-inventory-skeleton--status" aria-hidden="true"></span><span class="visually-hidden">Loading availability</span>';
      var weightSkeleton =
        skeletonApi && typeof skeletonApi.getInventorySkeletonMarkup === 'function'
          ? skeletonApi.getInventorySkeletonMarkup('weight-action')
          : '<span class="aone-inventory-skeleton aone-inventory-skeleton--weight-action" aria-hidden="true"></span><span class="visually-hidden">Loading weight selection</span>';

      var qvStatusMod = !shopifyKnown ? 'loading' : shopifyInStock || cartQty > 0 ? 'available' : 'coming-soon';
      var stockEmpty = shopifyOut;
      var statusTextLabel = !shopifyKnown
        ? ''
        : shopifyInStock
          ? shopifyDisplay.label
          : cartQty > 0
            ? 'IN-STOCK: 0'
            : '';
      return {
        productId: productId,
        variant: variant,
        displaySku: displaySku,
        rowVal: rowVal,
        colVal: colVal,
        availableQty: availableQty,
        shopifyQty: shopifyQty,
        shopifyKnown: shopifyKnown,
        orderQtyMax: shopifyInStock ? shopifyQty : 0,
        cartQty: cartQty,
        status: status,
        showSkeleton: showSkeleton,
        minWeightLabel: weightActionLabel,
        materialValue: materialValue || '',
        rowAvailable: rowAvailable,
        cellState: cellState,
        wmsStatusAttr: wmsStatusAttr,
        qvStatusMod: qvStatusMod,
        statusSkeleton: statusSkeleton,
        weightSkeleton: weightSkeleton,
        stockEmpty: stockEmpty,
        statusContent: !shopifyKnown
          ? statusSkeleton
          : '<span class="quick-view-chain-matrix__status-text">' +
          escapeHtml(statusTextLabel) +
          '</span><button type="button" class="quick-view-chain-matrix__info-btn bulk-order-matrix__info-btn" data-premium-pdp-sku-tip data-tip-sku="' +
          escapeAttr(displaySku) +
          '" aria-label="' +
          escapeAttr(displaySku ? 'View SKU: ' + displaySku : 'View variant SKU') +
          '" aria-expanded="false"' +
          (displaySku ? '' : ' disabled') +
          '><svg class="quick-view-chain-matrix__info-icon" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" stroke-width="1.25"></circle><circle cx="8" cy="5.25" r="0.9" fill="currentColor"></circle><path d="M8 7.35v3.9" stroke="currentColor" stroke-width="1.35" stroke-linecap="round"></path></svg></button>',
        weightContent:
          '<span data-details-weight-label>' + escapeHtml(weightActionLabel) + '</span>',
      };
    }

    function renderMatrixVariantControls(cell) {
      // Confirmed live zero stays in the grid as a non-interactive grey cell.
      // Loading is not zero. In-stock and all-in-cart keep the existing controls.
      if (
        cell &&
        cell.stockEmpty === true
      ) {
        return (
          '<div class="premium-pdp-matrix__variant is-unavailable is-stock-empty" aria-hidden="true"></div>'
        );
      }
      var qtyDisabled = cell.rowAvailable ? '' : ' disabled';
      var weightDisabled = cell.rowAvailable && !cell.showSkeleton ? '' : ' disabled';
      var ariaDisabled = cell.rowAvailable ? '' : ' aria-disabled="true"';
      var hasCartSelection = Number(cell.cartQty) > 0;
      var shopifyKnown = cell.shopifyKnown === true;
      var shopifyComingSoon = shopifyKnown && Number(cell.shopifyQty) <= 0;
      var qtyValue = hasCartSelection ? String(Math.floor(Number(cell.cartQty) || 0)) : '0';
      var qtyClass = !shopifyKnown ? ' premium-pdp-matrix__qty-input--loading' : '';
      var variantExtraClass = hasCartSelection ? ' has-weight-selected is-selected' : '';
      if (shopifyComingSoon && !cell.rowAvailable) {
        variantExtraClass += ' is-unavailable';
      }
      var weightBtnExtraClass = hasCartSelection ? ' is-confirmed' : '';
      var safeRowVal = escapeAttr(cell.rowVal);
      var safeColVal = escapeAttr(cell.colVal);
      var safeAriaLabel = escapeAttr('Quantity for ' + cell.rowVal + ' x ' + cell.colVal);
      var safeWeightLabel = escapeAttr(
        hasCartSelection ? 'Edit weight for ' + cell.rowVal + ' and ' + cell.colVal : 'Select weight for ' + cell.rowVal + ' and ' + cell.colVal
      );
      return (
        '<div class="premium-pdp-matrix__variant' +
        variantExtraClass +
        '" data-details-variant-row data-variant-id="' +
        cell.variant.id +
        '" data-product-id="' +
        escapeAttr(cell.productId) +
        '" data-source-product-id="' +
        escapeAttr(cell.variant.product_id || cell.variant.productId || cell.productId) +
        '" data-variant-sku="' +
        escapeAttr(cell.displaySku) +
        '" data-sku="' +
        escapeAttr(cell.displaySku) +
        '" data-variant-price-cents="' +
        (Number(cell.variant.price) || 0) +
        '" data-variant-available="' +
        (cell.rowAvailable ? 'true' : 'false') +
        '" data-row-option-value="' +
        safeRowVal +
        '" data-column-option-value="' +
        safeColVal +
        '" data-width="' +
        safeRowVal +
        '" data-length="' +
        safeColVal +
        '" data-selected-quantity="' +
        (hasCartSelection ? String(Math.floor(Number(cell.cartQty) || 0)) : '0') +
        '" data-cell-state="' +
        cell.cellState +
        '" data-matrix-width="' +
        safeRowVal +
        '" data-matrix-length="' +
        safeColVal +
        '" data-variant-inventory-qty="' +
        (Number(cell.orderQtyMax) || 0) +
        '"' +
        (cell.shopifyKnown
          ? ' data-shopify-inventory-qty="' +
            Math.max(0, Math.floor(Number(cell.shopifyQty) || 0)) +
            '"'
          : '') +
        ' data-wms-status="' +
        cell.wmsStatusAttr +
        '" data-jewelry-material="' +
        escapeAttr(cell.materialValue) +
        '">' +
        '<div class="premium-pdp-matrix__status bulk-order-matrix__status quick-view-chain-matrix__status premium-pdp-matrix__status--' +
        (cell.qvStatusMod === 'available'
          ? 'available'
          : cell.qvStatusMod === 'loading'
            ? 'loading'
            : 'coming-soon') +
        ' quick-view-chain-matrix__status--' +
        (cell.qvStatusMod || 'loading') +
        '" role="status" aria-live="polite">' +
        cell.statusContent +
        '</div>' +
        '<div class="premium-pdp-matrix__qty-wrap" role="group" aria-label="' +
        safeAriaLabel +
        '"' +
        ariaDisabled +
        '>' +
        '<button type="button" class="order-sheet-details-qty__btn premium-pdp-matrix__qty-btn" data-details-qty-minus aria-label="Decrease quantity"' +
        qtyDisabled +
        '>−</button>' +
        '<input type="number" class="order-sheet-details-qty__input premium-pdp-matrix__qty-input' +
        qtyClass +
        '" data-details-qty-input value="' +
        qtyValue +
        '" min="0" max="' +
        (Math.max(0, Number(cell.orderQtyMax) || 0) + (hasCartSelection ? Math.max(0, Math.floor(Number(cell.cartQty) || 0)) : 0)) +
        '" step="1" inputmode="numeric" aria-label="' +
        safeAriaLabel +
        '"' +
        qtyDisabled +
        '>' +
        '<button type="button" class="order-sheet-details-qty__btn premium-pdp-matrix__qty-btn" data-details-qty-plus aria-label="Increase quantity"' +
        qtyDisabled +
        '>+</button>' +
        '</div>' +
        '<div class="premium-pdp-matrix__weight-wrap">' +
        '<button type="button" class="order-sheet-details-variant-table__weight-btn premium-pdp-matrix__weight-btn' +
        weightBtnExtraClass +
        '" data-details-weight-open data-weight-idle-label="' +
        escapeAttr(cell.minWeightLabel || 'Select Weight') +
        '" aria-haspopup="dialog" aria-label="' +
        safeWeightLabel +
        '"' +
        weightDisabled +
        '>' +
        cell.weightContent +
        '</button>' +
        '</div></div>'
      );
    }

    function renderMatrixMobileCards(matrixRoot, matrixData, selectedContextValue, cellsByRow) {
      var mobile = matrixRoot.querySelector('[data-premium-matrix-mobile]');
      if (!(mobile instanceof HTMLElement)) return;
      var rowLabel = getAxisLabel(matrixData.rowDef);
      var colLabel = getAxisLabel(matrixData.colDef);
      var html = '';
      matrixData.rowValues.forEach(function (rowVal) {
        var cells = cellsByRow[rowVal] || [];
        html +=
          '<details class="premium-pdp-matrix-mobile__group" open>' +
          '<summary class="premium-pdp-matrix-mobile__summary">' +
          escapeHtml(rowLabel) +
          ': ' +
          escapeHtml(rowVal) +
          '</summary>' +
          '<div class="premium-pdp-matrix-mobile__cards">';
        cells.forEach(function (entry) {
          if (entry.missing) {
            // Skip missing variants on mobile — no card for empty Width×Length combinations.
            return;
          }
          html +=
            '<article class="premium-pdp-matrix-mobile__card premium-pdp-matrix-mobile__card--' +
            entry.cell.cellState +
            '">' +
            '<p class="premium-pdp-matrix-mobile__meta">' +
            escapeHtml(colLabel) +
            ': ' +
            escapeHtml(entry.colVal) +
            '</p>' +
            renderMatrixVariantControls(entry.cell) +
            '</article>';
        });
        html += '</div></details>';
      });
      mobile.innerHTML = html;
    }

    function syncMatrixLayoutMode(matrixRoot) {
      if (!(matrixRoot instanceof HTMLElement)) return;
      var mobileMode = isMatrixMobileViewport();
      var scroll = matrixRoot.querySelector('.premium-pdp-matrix__scroll');
      var mobile = matrixRoot.querySelector('[data-premium-matrix-mobile]');
      if (scroll instanceof HTMLElement) {
        scroll.hidden = mobileMode;
        if (mobileMode) scroll.setAttribute('inert', '');
        else scroll.removeAttribute('inert');
      }
      if (mobile instanceof HTMLElement) {
        mobile.hidden = !mobileMode;
        if (!mobileMode) mobile.setAttribute('inert', '');
        else mobile.removeAttribute('inert');
      }
    }

    function renderMatrix(root, product, detailsRoot, matrixData, selectedContextValue, wmsBySku, options) {
      options = options || {};
      var isLoading = options.isLoading === true;
      var matrixRoot = root.querySelector('[data-premium-pdp-matrix]');
      if (!(matrixRoot instanceof HTMLElement)) return;
      var head = matrixRoot.querySelector('[data-premium-matrix-head]');
      var body = matrixRoot.querySelector('[data-premium-matrix-body]');
      var countEl = matrixRoot.querySelector('[data-premium-matrix-variant-count]');
      if (!(head instanceof HTMLElement) || !(body instanceof HTMLElement) || !(countEl instanceof HTMLElement)) return;

      matrixRenderStateByRoot.set(root, {
        product: product,
        detailsRoot: detailsRoot,
        matrixData: matrixData,
        selectedContextValue: selectedContextValue,
        wmsBySku: wmsBySku,
        options: options,
      });
      bindMatrixViewportListener(root);

      var mobileMode = isMatrixMobileViewport();
      var rowLabel = getAxisLabel(matrixData.rowDef);
      var colLabel = getAxisLabel(matrixData.colDef);
      var colCount = matrixData.colValues.length;
      var headerHtml =
        '<tr><th scope="col" rowspan="2" class="premium-pdp-matrix__axis-corner"><span class="premium-pdp-matrix__axis-corner-label">' +
        escapeHtml(rowLabel) +
        '</span></th>' +
        '<th scope="col" colspan="' +
        colCount +
        '" class="premium-pdp-matrix__axis-group">' +
        escapeHtml(colLabel) +
        '</th></tr><tr>';
      matrixData.colValues.forEach(function (colVal) {
        headerHtml +=
          '<th scope="col" class="premium-pdp-matrix__axis-column">' + escapeHtml(colVal) + '</th>';
      });
      headerHtml += '</tr>';
      head.innerHTML = headerHtml;

      var matrixVariantCount = 0;
      var bodyHtml = '';
      /** @type {Record<string, Array<{ missing?: boolean, colVal: string, cell?: any }>>} */
      var cellsByRow = Object.create(null);

      matrixData.rowValues.forEach(function (rowVal) {
        cellsByRow[rowVal] = [];
        bodyHtml +=
          '<tr><th scope="row" class="premium-pdp-matrix__axis-row">' + escapeHtml(rowVal) + '</th>';
        matrixData.colValues.forEach(function (colVal) {
          var key = [selectedContextValue || '', rowVal, colVal].join('||');
          var variant = matrixData.map.get(key);
          if (!variant) {
            cellsByRow[rowVal].push({ missing: true, colVal: colVal });
            bodyHtml +=
              '<td class="premium-pdp-matrix__cell premium-pdp-matrix__cell--missing"><div class="premium-pdp-matrix__cell-empty" aria-hidden="true"></div></td>';
            return;
          }

          matrixVariantCount += 1;
          var cell = buildMatrixCellModel(product, variant, rowVal, colVal, wmsBySku, {
            isLoading: isLoading,
            matrixData: matrixData,
          });
          cellsByRow[rowVal].push({ colVal: colVal, cell: cell });
          bodyHtml +=
            '<td class="premium-pdp-matrix__cell premium-pdp-matrix__cell--' +
            cell.cellState +
            (cell.stockEmpty ? ' premium-pdp-matrix__cell--stock-empty' : '') +
            '">' +
            renderMatrixVariantControls(cell) +
            '</td>';
        });
        bodyHtml += '</tr>';
      });

      // Only one layout is interactive: avoid duplicate variant rows in DOM totals/ATC.
      if (mobileMode) {
        body.innerHTML = '';
        renderMatrixMobileCards(matrixRoot, matrixData, selectedContextValue, cellsByRow);
      } else {
        body.innerHTML = bodyHtml;
        var mobileHost = matrixRoot.querySelector('[data-premium-matrix-mobile]');
        if (mobileHost instanceof HTMLElement) mobileHost.innerHTML = '';
      }

      countEl.textContent =
        '(' + matrixVariantCount + (matrixVariantCount === 1 ? ' Variant' : ' Variants') + ')';
      syncMatrixLayoutMode(matrixRoot);
      bindMatrixStickyHeaderSync(matrixRoot);
    }

    function setupMatrixContext(root, detailsRoot, matrixData, product, wmsBySku, options) {
      options = options || {};
      var contextDef = matrixData.contextDef;
      var contextWrap = root.querySelector('[data-premium-matrix-context]');
      var contextLabel = root.querySelector('[data-premium-matrix-context-label]');
      var contextSelect = root.querySelector('[data-premium-matrix-context-select]');
      var contextSingle = root.querySelector('[data-premium-matrix-context-single]');
      var meaningfulContextValues = getMeaningfulContextValues(matrixData.contextValues);
      var preferredContext = String(options.preferredContext || '').trim();
      var selectedContext =
        preferredContext && meaningfulContextValues.indexOf(preferredContext) >= 0
          ? preferredContext
          : meaningfulContextValues[0] || '';

      if (
        contextDef &&
        meaningfulContextValues.length > 1 &&
        contextWrap instanceof HTMLElement &&
        contextSelect instanceof HTMLSelectElement
      ) {
        contextWrap.hidden = false;
        if (contextLabel instanceof HTMLElement) contextLabel.textContent = getAxisLabel(contextDef);
        contextSelect.innerHTML = meaningfulContextValues.map(function (value) {
          return '<option value="' + escapeAttr(value) + '">' + escapeHtml(value) + '</option>';
        }).join('');
        contextSelect.value = selectedContext;
        contextSelect.onchange = function () {
          clearBulkSelections(detailsRoot);
          selectedContext = contextSelect.value;
          renderMatrix(root, product, detailsRoot, matrixData, selectedContext, wmsBySku, options);
          syncPdpDraftFromCart(detailsRoot, { isLoading: options.isLoading === true, force: true });
        };
        if (contextSingle instanceof HTMLElement) contextSingle.hidden = true;
      } else {
        if (contextWrap instanceof HTMLElement) contextWrap.hidden = true;
        if (contextSingle instanceof HTMLElement) {
          /* PDP matrix does not show the single-value material line (MATERIAL: 14KT). */
          contextSingle.hidden = true;
          contextSingle.textContent = '';
          if (contextDef && meaningfulContextValues.length === 1) {
            selectedContext = meaningfulContextValues[0];
          }
        }
      }

      renderMatrix(root, product, detailsRoot, matrixData, selectedContext, wmsBySku, options);
      syncPdpDraftFromCart(detailsRoot, { isLoading: options.isLoading === true, force: true });
    }

    function hydrateListRows(root, product, detailsRoot, options) {
      options = options || {};
      var isLoading = options.isLoading === true || product.__pdpProductsBatchResolved !== true;
      var rows = Array.prototype.slice.call(detailsRoot.querySelectorAll('[data-details-variant-row]'));
      var variantById = Object.create(null);
      (product.variants || []).forEach(function (variant) {
        if (!variant || variant.id == null) return;
        variantById[String(variant.id)] = variant;
      });

      rows.forEach(function (row) {
        if (!(row instanceof HTMLElement)) return;
        var displaySku = normalizeSkuDisplay(row.dataset.variantSku);
        var variant = variantById[String(row.dataset.variantId || '')] || null;

        if (isLoading) {
          updatePdpVariantRowFromWms(row, null, { isLoading: true, variant: variant });
          return;
        }

        if (!displaySku) {
          updatePdpVariantRowFromWms(row, null, { isLoading: false, variant: variant });
          return;
        }

        var node = getCachedNodeForSku(product, displaySku);
        if (node && node.__pdpInventoryPending) {
          updatePdpVariantRowFromWms(row, null, { isLoading: true, variant: variant });
          return;
        }
        if (!node) {
          warnPdpDev('No WMS SKU match for PDP row', {
            productId: normalizeShopifyId(product && product.id),
            sku: displaySku,
            variantId: row.dataset.variantId || '',
          });
        }
        var wmsSkuData = normalizePdpWmsSkuData(node, displaySku);
        updatePdpVariantRowFromWms(row, wmsSkuData, { isLoading: false, variant: variant });
      });

      syncPdpDraftFromCart(detailsRoot, { isLoading: isLoading, force: true });
      if (
        window.plpVariantDetails &&
        typeof window.plpVariantDetails.syncPremiumPdpListEmptyState === 'function'
      ) {
        window.plpVariantDetails.syncPremiumPdpListEmptyState(detailsRoot);
      }
      if (typeof detailsRoot.dispatchEvent === 'function') {
        detailsRoot.dispatchEvent(new CustomEvent('plp-details-qty-update', { bubbles: true }));
      }
    }

    function initVariantTable(root, product) {
      stampShopifyQtyFromLiquidVariants(product);
      product.__pdpProductsBatchResolved = false;
      var panel = root.querySelector('[data-pdp-panel] .plp-view-host[data-plp-details-panel], .plp-view-host[data-plp-details-panel]');
      var detailsRoot = panel && panel.querySelector('[data-plp-expanded-details]');
      if (!(panel instanceof HTMLElement) || !(detailsRoot instanceof HTMLElement)) return;
      var matrixRoot = root.querySelector('[data-premium-pdp-matrix]');
      var listRoot = root.querySelector('[data-premium-pdp-list]');
      var optionDefs = product.__linkedFamilyMerged ? optionDefsFromProduct(product) : parseOptionDefinitions(root);
      if (!optionDefs.length) optionDefs = parseOptionDefinitions(root);
      var matrixConfig = detectMatrixConfig(optionDefs, product);
      var layoutMode = getInitialLayoutMode(root, optionDefs, product);
      var useMatrix = layoutMode === 'matrix' && !!matrixConfig && matrixRoot instanceof HTMLElement;
      root.dataset.variantLayout = useMatrix ? 'matrix' : 'table';
      root.classList.toggle('premium-pdp--matrix', useMatrix);
      root.classList.toggle('premium-pdp--table', !useMatrix);

      var productId = normalizeShopifyId(product && product.id);
      var wmsApi = getWmsApi();
      if (wmsApi && typeof wmsApi.registerVariant === 'function') {
        (product.variants || []).forEach(function (variant) {
          if (!variant) return;
          var sku = normalizeSkuDisplay(variant.sku);
          if (!sku) return;
          var ownerId = normalizeShopifyId(variant.product_id || variant.productId) || productId;
          wmsApi.registerVariant(String(variant.id), ownerId, sku);
        });
      }

      if (useMatrix) {
        if (listRoot instanceof HTMLElement) listRoot.hidden = true;
        if (matrixRoot instanceof HTMLElement) matrixRoot.hidden = false;
        var initialMatrixData = buildMatrixData(product, matrixConfig);
        if (initialMatrixData.rowValues.length > 0 && initialMatrixData.colValues.length > 0) {
          setupMatrixContext(root, detailsRoot, initialMatrixData, product, new Map(), { isLoading: true });
        } else if (matrixRoot instanceof HTMLElement) {
          var initialBody = matrixRoot.querySelector('[data-premium-matrix-body]');
          if (initialBody instanceof HTMLElement) {
            initialBody.innerHTML = '<tr><td colspan="999" class="premium-pdp-matrix__error-cell">No matrix variants available.</td></tr>';
          }
        }
      } else {
        if (matrixRoot instanceof HTMLElement) matrixRoot.hidden = true;
        if (listRoot instanceof HTMLElement) listRoot.hidden = false;
        hydrateListRows(root, product, detailsRoot, { isLoading: true });
      }

      if (window.plpVariantDetails && typeof window.plpVariantDetails.bindPanel === 'function') {
        window.plpVariantDetails.bindPanel(panel);
      }

      var onInteraction = function (event) {
        var target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        var row = target.closest('[data-details-variant-row]');
        if (!(row instanceof HTMLElement)) return;
        var sku = normalizeSkuDisplay(row.dataset.variantSku);
        if (sku) setActiveSku(root, sku);
      };
      detailsRoot.addEventListener('click', onInteraction, true);
      detailsRoot.addEventListener('input', onInteraction, true);
      detailsRoot.addEventListener('change', onInteraction, true);

      var hydrateGeneration = (hydrateGenerationByRoot.get(root) || 0) + 1;
      hydrateGenerationByRoot.set(root, hydrateGeneration);

      var paintMatrixOrList = function (paintOptions) {
        paintOptions = paintOptions || {};
        var weightStillLoading = paintOptions.isLoading === true;
        if (useMatrix && matrixConfig && matrixRoot instanceof HTMLElement) {
          var wmsBySku = weightStillLoading ? new Map() : getCachedWmsBySku(product);
          var matrixData = buildMatrixData(product, matrixConfig);
          if (matrixData.rowValues.length > 0 && matrixData.colValues.length > 0) {
            matrixRoot.hidden = false;
            if (listRoot instanceof HTMLElement) listRoot.hidden = true;
            setupMatrixContext(root, detailsRoot, matrixData, product, wmsBySku, {
              isLoading: weightStillLoading,
            });
            if (!weightStillLoading) {
              syncPdpDraftFromCart(detailsRoot, { force: true });
            }
            return;
          }
          if (matrixRoot instanceof HTMLElement) matrixRoot.hidden = true;
          if (listRoot instanceof HTMLElement) listRoot.hidden = false;
          hydrateListRows(root, product, detailsRoot, { isLoading: weightStillLoading });
          return;
        }
        if (matrixRoot instanceof HTMLElement) matrixRoot.hidden = true;
        if (listRoot instanceof HTMLElement) listRoot.hidden = false;
        hydrateListRows(root, product, detailsRoot, { isLoading: weightStillLoading });
      };

      // Shopify inventory paints as soon as qty view resolves; weight waits for products-batch.
      ensureShopifyInventoryOnProduct(root, product).then(function () {
        if (hydrateGenerationByRoot.get(root) !== hydrateGeneration) return;
        if (!(root.isConnected)) return;
        var jsonElEarly = root.querySelector('[data-pdp-product-json]');
        if (jsonElEarly) {
          try {
            jsonElEarly.textContent = JSON.stringify(product);
          } catch (_err) {
            /* noop */
          }
        }
        // Weight still pending when batch has not resolved yet.
        paintMatrixOrList({ isLoading: product.__pdpProductsBatchResolved !== true });
        syncMergedChainAvailabilityBadge(root, product);
      });

      ensureWmsProductData(product)
        .then(function () {
          if (hydrateGenerationByRoot.get(root) !== hydrateGeneration) return;
          if (!(root.isConnected)) return;
          // ensureWmsProductData owns __pdpProductsBatchResolved (only true after batch/cache).
          if (product.__pdpProductsBatchResolved !== true) return;
          var jsonEl = root.querySelector('[data-pdp-product-json]');
          if (jsonEl) {
            try {
              jsonEl.textContent = JSON.stringify(product);
            } catch (_err) {
              /* noop */
            }
          }

          var availabilityApi = getWmsApi();
          var cartReady =
            !availabilityApi ||
            typeof availabilityApi.isCartStateReady !== 'function' ||
            availabilityApi.isCartStateReady();
          if (!cartReady) {
            // Keep weight Checking… until cart reconciles; Shopify stock already painted.
            paintMatrixOrList({ isLoading: true });
            syncMergedChainAvailabilityBadge(root, product);
            return;
          }

          paintMatrixOrList({ isLoading: false });
          setStatus(root, '', false);
          syncMergedChainAvailabilityBadge(root, product);
        })
        .catch(function (error) {
          if (hydrateGenerationByRoot.get(root) !== hydrateGeneration) return;
          warnPdpDev('PDP products-batch hydrate failed', {
            productId: productId,
            error: error && error.message ? error.message : error,
          });
          syncMergedChainAvailabilityBadge(root, product);
          if (matrixRoot instanceof HTMLElement && useMatrix) {
            var body = matrixRoot.querySelector('[data-premium-matrix-body]');
            if (body instanceof HTMLElement) {
              body.innerHTML = '<tr><td colspan="999" class="premium-pdp-matrix__error-cell">Unable to load variant details. Please try again.</td></tr>';
            }
            if (listRoot instanceof HTMLElement) listRoot.hidden = true;
            matrixRoot.hidden = false;
          } else {
            var rows = Array.prototype.slice.call(detailsRoot.querySelectorAll('[data-details-variant-row]'));
            rows.forEach(function (row) {
              if (!(row instanceof HTMLElement)) return;
              row.dataset.variantAvailable = 'false';
              row.dataset.variantInventoryQty = '0';
              row.dataset.wmsStatus = 'error';
              row.classList.add('is-unavailable');
              row.dataset.rowState = 'error';
              var minWeightCell = row.querySelector('[data-premium-pdp-min-weight]');
              var stockCell = row.querySelector('[data-premium-pdp-stock]');
              var errVariant = null;
              var errVariants = Array.isArray(product.variants) ? product.variants : [];
              for (var vi = 0; vi < errVariants.length; vi += 1) {
                if (errVariants[vi] && String(errVariants[vi].id) === String(row.dataset.variantId || '')) {
                  errVariant = errVariants[vi];
                  break;
                }
              }
              if (minWeightCell) minWeightCell.textContent = '—';
              if (stockCell) {
                var errShopifyQty = resolveVariantShopifyQty(errVariant);
                stockCell.textContent = getShopifyDisplayStockStatus(errShopifyQty, {
                  numericOnly: true,
                }).label;
              }
              delete row.dataset.weightValue;
              delete row.dataset.serialNumbersValue;
              delete row.dataset.wmsOptionsJson;
              row.classList.remove('has-weight-selected');
              syncRowControls(row);
            });
            resetSummary(detailsRoot);
          }
          setStatus(root, 'Inventory details are currently unavailable. Please try again.', true);
        });

      var controller = new AbortController();
      controllersByRoot.set(root, controller);
    }

    function resolveVariantById(product, variantIdValue) {
      var variantId = Number(variantIdValue);
      if (!Number.isFinite(variantId)) return null;
      var variants = Array.isArray(product && product.variants) ? product.variants : [];
      for (var i = 0; i < variants.length; i += 1) {
        var variant = variants[i];
        if (Number(variant && variant.id) === variantId) return variant;
      }
      return null;
    }

    function setPdpSubmitState(button, mode, label) {
      if (!(button instanceof HTMLButtonElement)) return;
      var text = button.querySelector('[data-pdp-atc-label]');
      if (text instanceof HTMLElement) text.textContent = label;
      var disabled = mode !== 'available';
      button.disabled = disabled;
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      button.setAttribute('aria-busy', mode === 'loading' ? 'true' : 'false');
      button.dataset.wmsStatus = mode;
    }

    function initStandardPdpFormGuard(root, product) {
      var form = root.querySelector('[data-pdp-product-form]');
      if (!(form instanceof HTMLFormElement)) return;
      if (root.querySelector('[data-premium-pdp-list], [data-premium-pdp-matrix]')) return;
      var submit = form.querySelector('[data-pdp-atc-submit]');
      if (!(submit instanceof HTMLButtonElement)) return;
      var variantIdInput = form.querySelector('[data-pdp-variant-id]');
      if (!(variantIdInput instanceof HTMLInputElement)) return;
      var availabilityApi = window.AOneWmsAvailability;
      if (!availabilityApi) return;

      var readContext = function () {
        var variant = resolveVariantById(product, variantIdInput.value);
        var productId = normalizeShopifyId(product && product.id);
        var sku = normalizeSku(variant && variant.sku);
        if (variant && sku) {
          availabilityApi.registerVariant(String(variant.id), productId, sku);
        }
        return {
          productId: productId,
          variantId: variant ? String(variant.id) : String(variantIdInput.value || ''),
          sku: sku,
        };
      };

      var syncState = function () {
        setPdpSubmitState(submit, 'loading', 'CHECKING AVAILABILITY');
        var context = readContext();
        availabilityApi
          .guardVariantAdd({
            productId: context.productId,
            variantId: context.variantId,
            sku: context.sku,
            quantity: Number((form.querySelector('input[name="quantity"]') || {}).value || 1) || 1,
          })
          .then(function (guard) {
            if (guard && guard.ok) {
              setPdpSubmitState(submit, 'available', 'ADD TO CART');
              return;
            }
            if (guard && guard.type === 'coming-soon') {
              setPdpSubmitState(submit, 'coming-soon', 'COMING SOON');
              return;
            }
            if (guard && guard.type === 'loading') {
              setPdpSubmitState(submit, 'loading', 'CHECKING AVAILABILITY');
              return;
            }
            setPdpSubmitState(submit, 'error', 'AVAILABILITY UNAVAILABLE');
          })
          .catch(function () {
            setPdpSubmitState(submit, 'error', 'AVAILABILITY UNAVAILABLE');
          });
      };

      syncState();
      form.addEventListener('change', syncState);

      form.addEventListener('submit', function (event) {
        if (form.dataset.wmsValidatedSubmit === 'true') return;
        event.preventDefault();
        event.stopPropagation();
        setPdpSubmitState(submit, 'loading', 'CHECKING AVAILABILITY');
        var context = readContext();
        availabilityApi
          .guardVariantAdd({
            productId: context.productId,
            variantId: context.variantId,
            sku: context.sku,
            quantity: Number((form.querySelector('input[name="quantity"]') || {}).value || 1) || 1,
            forceCartRefresh: true,
          })
          .then(function (guard) {
            if (!guard || !guard.ok) {
              if (guard && guard.type === 'coming-soon') {
                setPdpSubmitState(submit, 'coming-soon', 'COMING SOON');
              } else if (guard && guard.type === 'loading') {
                setPdpSubmitState(submit, 'loading', 'CHECKING AVAILABILITY');
              } else {
                setPdpSubmitState(submit, 'error', 'AVAILABILITY UNAVAILABLE');
              }
              setStatus(root, guard && guard.message ? guard.message : 'Availability unavailable', true);
              return;
            }
            setStatus(root, '', false);
            form.dataset.wmsValidatedSubmit = 'true';
            form.submit();
          })
          .catch(function () {
            setPdpSubmitState(submit, 'error', 'AVAILABILITY UNAVAILABLE');
            setStatus(root, 'Availability unavailable', true);
          });
      });
    }

    function boot(root) {
      if (!(root instanceof HTMLElement)) return;
      if (root.dataset.premiumPdpInitialized === 'true') return;
      root.dataset.premiumPdpInitialized = 'true';
      bindPdpSkuCopyTooltips();
      var product = applyLinkedFamilyToProduct(root, parseProduct(root));
      if (!product) return;
      initGallery(root, product);
      initVariantTable(root, product);
      initStandardPdpFormGuard(root, product);
      initAccordion(root);
    }

    function destroy(root) {
      var controller = controllersByRoot.get(root);
      if (controller) controller.abort();
      controllersByRoot.delete(root);
      if (
        pdpSkuTooltipState.activeTrigger instanceof HTMLElement &&
        root instanceof HTMLElement &&
        root.contains(pdpSkuTooltipState.activeTrigger)
      ) {
        hidePdpSkuTooltip();
      }
      if (root instanceof HTMLElement) {
        hydrateGenerationByRoot.set(root, (hydrateGenerationByRoot.get(root) || 0) + 1);
        delete root.dataset.premiumPdpInitialized;
      }
    }

    function initAll() {
      bindPdpSkuCopyTooltips();
      document.querySelectorAll('[data-pdp-section]').forEach(boot);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAll, { once: true });
    } else {
      initAll();
    }

    function rehydratePdpInventoryFromCart() {
      document.querySelectorAll('[data-pdp-section]').forEach(function (root) {
        if (!(root instanceof HTMLElement)) return;
        var detailsRoot =
          root.querySelector('[data-premium-pdp-details], [data-plp-expanded-details], [data-details-root]') ||
          root;
        if (!(detailsRoot instanceof HTMLElement)) return;
        var product = parseProduct(root);
        if (!product) return;

        Promise.all([ensureShopifyInventoryOnProduct(root, product), ensureWmsProductData(product)]).then(
          function () {
            if (product.__pdpProductsBatchResolved !== true) return;
            var availabilityApi = getWmsApi();
            var cartReady =
              !availabilityApi ||
              typeof availabilityApi.isCartStateReady !== 'function' ||
              availabilityApi.isCartStateReady();

            var matrixRoot = root.querySelector('[data-premium-pdp-matrix]');
            var listRoot = root.querySelector('[data-premium-pdp-list]');
            var optionDefs = product.__linkedFamilyMerged ? optionDefsFromProduct(product) : parseOptionDefinitions(root);
            if (!optionDefs.length) optionDefs = parseOptionDefinitions(root);
            var matrixConfig = detectMatrixConfig(optionDefs, product);
            var useMatrix =
              root.dataset.variantLayout === 'matrix' &&
              !!matrixConfig &&
              matrixRoot instanceof HTMLElement &&
              !matrixRoot.hidden;

            // Preserve context selection across cart-driven rehydrates.
            var contextSelect = root.querySelector('[data-premium-matrix-context-select]');
            var preservedContext =
              contextSelect instanceof HTMLSelectElement ? String(contextSelect.value || '') : '';

            if (!cartReady) {
              if (useMatrix && matrixConfig) {
                var pendingMatrix = buildMatrixData(product, matrixConfig);
                if (pendingMatrix.rowValues.length > 0 && pendingMatrix.colValues.length > 0) {
                  setupMatrixContext(root, detailsRoot, pendingMatrix, product, new Map(), {
                    isLoading: true,
                    preferredContext: preservedContext,
                  });
                }
              } else {
                hydrateListRows(root, product, detailsRoot, { isLoading: true });
              }
              syncMergedChainAvailabilityBadge(root, product);
              return;
            }

            if (useMatrix && matrixConfig) {
              var wmsBySku = getCachedWmsBySku(product);
              var matrixData = buildMatrixData(product, matrixConfig);
              if (matrixData.rowValues.length > 0 && matrixData.colValues.length > 0) {
                setupMatrixContext(root, detailsRoot, matrixData, product, wmsBySku, {
                  isLoading: false,
                  preferredContext: preservedContext,
                });
                // setupMatrixContext already hydrates cart → draft + footer.
                if (typeof detailsRoot.dispatchEvent === 'function') {
                  detailsRoot.dispatchEvent(new CustomEvent('plp-details-qty-update', { bubbles: true }));
                }
                syncMergedChainAvailabilityBadge(root, product);
                return;
              }
            }
            if (listRoot instanceof HTMLElement || !useMatrix) {
              hydrateListRows(root, product, detailsRoot, { isLoading: false });
              syncMergedChainAvailabilityBadge(root, product);
            }
          }
        );
      });
    }

    if (document.documentElement.dataset.aonePdpCartInventoryBound !== 'true') {
      document.documentElement.dataset.aonePdpCartInventoryBound = 'true';
      var availabilityApi = window.AOneWmsAvailability || window.WMSInventory;
      var eventName =
        (availabilityApi && availabilityApi.CART_INVENTORY_EVENT) || 'aone:cart-inventory-updated';
      document.addEventListener(eventName, function (event) {
        var detail = event && event.detail ? event.detail : null;
        if (detail && (detail.loading === true || detail.cartReady === false)) {
          rehydratePdpInventoryFromCart();
          return;
        }
        rehydratePdpInventoryFromCart();
      });
    }

    window.aonePremiumPdpRehydrateInventory = rehydratePdpInventoryFromCart;

    document.addEventListener('shopify:section:load', function (event) {
      var section = event.target instanceof Element ? event.target : null;
      if (!section) return;
      section.querySelectorAll('[data-pdp-section]').forEach(boot);
    });

    document.addEventListener('shopify:section:unload', function (event) {
      var section = event.target instanceof Element ? event.target : null;
      if (!section) return;
      section.querySelectorAll('[data-pdp-section]').forEach(destroy);
    });
  })();
